import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  CircleCheck,
  FileText,
  Pencil,
  Check,
  Eye,
  EyeOff,
  X,
  Trash2,
  Send,
  Mail,
  Phone,
  Building2,
  User,
  Users,
  TrendingUp,
  Briefcase,
  Loader2,
  Link2,
  Copy,
  Plus,
  Package,
  StickyNote,
  Wand2,
  Calculator,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Paperclip,
  ExternalLink,
  Download,
  FileSignature,
  Languages,
  Gift,
  CalendarClock,
  Globe,
  Linkedin,
  MapPin,
  Banknote,
  Upload,
  AlertTriangle,
  Reply,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { PdfPreview } from "@/components/PdfPreview";
import { DocxPreview } from "@/components/DocxPreview";
import { downloadBase64File, readFileAsBase64 } from "@/lib/download";
import { toLithuanianVocative } from "@/lib/lithuanian";
import { computeLineTotalCents, priceTiersAreFlatFee } from "@server/services/pricing";
import { AddContactWithAIDialog } from "@/components/add-contact-with-ai-dialog";
import { ContactFormDialog, type ContactFormInitial } from "@/components/contact-form-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { isWeekend } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor, isRichTextEmpty } from "@/components/rich-text-editor";
import { Markdown } from "@/components/markdown";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { OpportunityStatusBar } from "@/components/opportunity-status-bar";
import { UserAvatar, userLabel } from "@/components/user-avatar";
import { OpportunitySigning } from "@/components/opportunity-signing";
import { type Perk } from "@/components/proposal/perks";
import {
  type CalculatorState,
  type ProductCatalogItem,
} from "@/components/proposal/calculator";
import { generateQuotePdfBase64, buildQuoteMetaFromVariables } from "@/lib/quote-document";

type IndustryType = "accounting" | "business";

const INDUSTRY_TYPE_LABELS: Record<IndustryType, { label: string; color: string }> = {
  accounting: { label: "Accounting", color: "bg-blue-100 text-blue-700 border-blue-300" },
  business: { label: "Business", color: "bg-amber-100 text-amber-800 border-amber-300" },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  opportunity: { label: "Opportunity", color: "bg-slate-100 text-slate-700" },
  ready: { label: "Ready", color: "bg-zinc-100 text-zinc-700" },
  approaching: { label: "Approaching", color: "bg-sky-100 text-sky-700" },
  quote_sent: { label: "Quote sent", color: "bg-blue-100 text-blue-700" },
  negotiation: { label: "Negotiation", color: "bg-amber-100 text-amber-800" },
  signing: { label: "Contract signing", color: "bg-violet-100 text-violet-800" },
  won: { label: "Won", color: "bg-emerald-100 text-emerald-800" },
  lost: { label: "Lost", color: "bg-rose-100 text-rose-700" },
};

const ALL_STATUSES = Object.keys(STATUS_LABELS) as Array<keyof typeof STATUS_LABELS>;

type Brief = {
  client?: { name?: string; company?: string; email?: string; phone?: string; industry?: string; size?: string };
  opportunity_summary?: string;
  context?: string;
  pain_points?: string[];
  needs?: string[];
  recommended_plan?: string;
  estimated_seats?: number;
  estimated_mrr_eur?: number;
  recommended_addons?: string[];
  open_questions?: string[];
  positioning_strategy?: string;
  next_steps?: string[];
};

type ProposalProduct = {
  stripe_product_id: string;
  stripe_price_id: string;
  qty: number;
  usage?: number;
  unit_amount: number;
  original_unit_amount?: number;
  discount_cents?: number;
  usage_type?: "licensed" | "metered";
  tiers_mode?: string | null;
  tiers?: Array<Record<string, unknown>> | null;
  selected?: boolean;
  // Per-opportunity visibility for catalog extras (hub services / onboarding).
  // When true the line is kept in the proposal state but moved out of the
  // active list into a collapsible "Hidden" area so irrelevant hub services
  // don't clutter the quote. Undefined/false means visible (the default).
  hidden?: boolean;
  name?: string;
  description?: string;
};

type ProposalCalculatorState = {
  billing_period?: "monthly" | "annual";
  main?: ProposalProduct[];
  extra?: ProposalProduct[];
  currency?: string;
};

type ProposalRowForQuote = {
  id: string;
  status: string;
  calculatorState?: ProposalCalculatorState | null;
  perks?: Perk[] | null;
  createdAt?: string | Date | null;
  expiresAt?: string | Date | null;
};

type SelectedProductCard = {
  stripe_product_id: string;
  qty: number;
  kind: "main" | "extra";
  reason?: string;
  source?: "manual" | "ai";
  discount_cents?: number;
};

type ProductInfo = {
  name: string;
  nameLt: string | null;
  nameEn: string | null;
  category: string | null;
  subCategory: string | null;
};

type BillingPeriod = "monthly" | "annual";

type QuoteLine = ProposalProduct & {
  selected?: boolean;
  kind: "main" | "extra";
};

type QuoteState = {
  billing_period: BillingPeriod;
  main: ProposalProduct[];
  extra: ProposalProduct[];
  currency: string;
};

const OPPORTUNITY_TABS = ["notes", "call_plan", "opportunity", "products", "client", "documents", "history"] as const;
type OpportunityTab = (typeof OPPORTUNITY_TABS)[number];
const DEFAULT_TAB: OpportunityTab = "notes";

const NEXT_ACTION_FMT = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatNextAction(date: Date | string | null | undefined): {
  label: string;
  tone: "overdue" | "today" | "upcoming";
} | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return null;
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const formatted = NEXT_ACTION_FMT.format(d);
  if (diffDays < 0) {
    const overdue = -diffDays;
    return {
      label: `${formatted} · ${overdue === 1 ? "1d overdue" : `${overdue}d overdue`}`,
      tone: "overdue",
    };
  }
  if (diffDays === 0) return { label: `${formatted} · today`, tone: "today" };
  if (diffDays === 1) return { label: `${formatted} · tomorrow`, tone: "upcoming" };
  return { label: formatted, tone: "upcoming" };
}

function isOpportunityTab(value: string | undefined): value is OpportunityTab {
  return !!value && (OPPORTUNITY_TABS as readonly string[]).includes(value);
}

export function OpportunityDetailPage() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const activeTab: OpportunityTab = isOpportunityTab(tab) ? tab : DEFAULT_TAB;

  useEffect(() => {
    if (!id) return;
    if (tab === "brief") {
      navigate(`/o/${id}/opportunity`, { replace: true });
      return;
    }
    if (tab && !isOpportunityTab(tab)) {
      navigate(`/o/${id}/${DEFAULT_TAB}`, { replace: true });
    }
  }, [id, tab, navigate]);

  const handleTabChange = (value: string) => {
    if (!id) return;
    navigate(`/o/${id}/${value}`);
  };
  const utils = trpc.useUtils();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [generateLanguage, setGenerateLanguage] = useState<"lt" | "en">("lt");
  const [rerenderLanguage, setRerenderLanguage] = useState<"lt" | "en">("lt");
  const [generateSections, setGenerateSections] = useState<Record<string, boolean>>({
    hero: true,
    pain_points: true,
    needs: true,
    scope: true,
    how_we_work: true,
    outcomes: true,
    timeline: true,
    why_us: true,
    case_studies: true,
    recommended: true,
    next_steps: true,
    calculator: true,
    open_questions: true,
    additional_services: true,
    vision: true,
    ai_agent: true,
    dashboard: true,
    integrations: true,
    feedback_loop: true,
    feedbackForm: true,
    footer: true,
  });
  const [rerenderProposalId, setRerenderProposalId] = useState<string | null>(null);
  // Briefly flag the row that just finished regenerating so the user gets a
  // clear "done" signal — the dialog closes immediately and the AI body runs
  // in the background, so without this the row gives no completion feedback.
  const [justRegeneratedId, setJustRegeneratedId] = useState<string | null>(null);
  const justRegeneratedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rerenderSections, setRerenderSections] = useState<Record<string, boolean>>({
    hero: true,
    pain_points: true,
    needs: true,
    scope: true,
    how_we_work: true,
    outcomes: true,
    timeline: true,
    why_us: true,
    case_studies: true,
    recommended: true,
    next_steps: true,
    calculator: true,
    open_questions: true,
    additional_services: true,
    vision: true,
    ai_agent: true,
    dashboard: true,
    integrations: true,
    feedback_loop: true,
    feedbackForm: true,
    footer: true,
  });
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  // When set, the message dialog opens pre-filled as a reply to a client's
  // inbound email (a "client_reply" note captured by the Gmail poller).
  const [replyContext, setReplyContext] = useState<{
    toEmail: string;
    subject: string | null;
  } | null>(null);
  const [regenAiOpen, setRegenAiOpen] = useState(false);
  const [regenAiInstruction, setRegenAiInstruction] = useState("");
  const [callPlanRegenOpen, setCallPlanRegenOpen] = useState(false);
  const [callPlanInstruction, setCallPlanInstruction] = useState("");

  const [newNote, setNewNote] = useState("");
  const opp = trpc.opportunities.get.useQuery({ id: id! }, { enabled: !!id });

  const proposalsList = trpc.proposals.list.useQuery(
    { opportunityId: id! },
    { enabled: !!id }
  );

  useEffect(() => {
    if (rerenderProposalId === null) return;
    const proposals = (proposalsList.data ?? []) as Array<{
      id: string;
      visibleSections?: string[] | null;
      landingFeatures?: { feedbackForm?: boolean } | null;
    }>;
    const current = proposals.find((p) => p.id === rerenderProposalId);
    if (!current) return;
    const vs = current.visibleSections;
    // Mirror the public page's logic (see public-proposal.tsx): only proposals
    // with no visibleSections, or the older id set, default every toggle to on.
    // Otherwise the saved array is authoritative so the toggles reflect exactly
    // what's shown on the public page. Keep this list in sync with the public
    // page — "open_questions" is a current key, NOT a legacy marker.
    const LEGACY_MARKERS = ["context", "positioning"];
    const isLegacyVs =
      Array.isArray(vs) && vs.some((id) => LEGACY_MARKERS.includes(id));
    const showAny = (id: string) => !vs || isLegacyVs || vs.includes(id);
    setRerenderSections({
      hero: showAny("hero"),
      pain_points: showAny("pain_points"),
      needs: showAny("needs"),
      scope: showAny("scope"),
      how_we_work: showAny("how_we_work"),
      outcomes: showAny("outcomes"),
      timeline: showAny("timeline"),
      why_us: showAny("why_us"),
      case_studies: showAny("case_studies"),
      recommended: showAny("recommended"),
      next_steps: showAny("next_steps"),
      calculator: showAny("calculator"),
      open_questions: showAny("open_questions"),
      additional_services: showAny("additional_services"),
      ai_agent: showAny("ai_agent"),
      dashboard: showAny("dashboard"),
      integrations: showAny("integrations"),
      feedback_loop: showAny("feedback_loop"),
      feedbackForm: current.landingFeatures?.feedbackForm !== false,
      footer: showAny("footer"),
    });
  }, [rerenderProposalId, proposalsList.data]);

  useEffect(() => {
    if (!templatePickerOpen) return;
    const lang = ((opp.data as { language?: "lt" | "en" } | undefined)?.language ?? "lt") as "lt" | "en";
    setGenerateLanguage(lang);
  }, [templatePickerOpen, opp.data]);

  useEffect(() => {
    if (rerenderProposalId === null) return;
    const lang = ((opp.data as { language?: "lt" | "en" } | undefined)?.language ?? "lt") as "lt" | "en";
    setRerenderLanguage(lang);
  }, [rerenderProposalId, opp.data]);

  const notesQuery = trpc.opportunities.notes.useQuery(
    { opportunityId: id! },
    { enabled: !!id }
  );
  const documentsQuery = trpc.documents.list.useQuery(
    { opportunityId: id! },
    { enabled: !!id }
  );
  const productsQuery = trpc.products.list.useQuery();

  // Product builder bootstrap mutations — used only to seed the very first
  // product into opp.selectedProducts before a proposal exists (which then
  // backs the auto-created skeleton proposal). Once a proposal exists the
  // quote builder is fully self-contained: it owns its product list via
  // proposal.calculatorState and never derives products from the opportunity,
  // the brief, or AI again.
  const deleteSelectedProduct = trpc.opportunities.deleteSelectedProduct.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to delete product"),
  });

  const addSelectedProduct = trpc.opportunities.addSelectedProduct.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to add product"),
  });

  const productsByStripeId = useMemo(() => {
    const map = new Map<string, ProductInfo>();
    for (const p of productsQuery.data ?? []) {
      map.set(p.stripeProductId, {
        name: p.name,
        nameLt: p.nameLt,
        nameEn: p.nameEn,
        category: p.category,
        subCategory: p.subCategory,
      });
    }
    return map;
  }, [productsQuery.data]);

  const proposedProducts = useMemo(() => {
    const proposals = proposalsList.data ?? [];
    if (proposals.length === 0) return [];
    const latest = proposals[proposals.length - 1] as { calculatorState?: ProposalCalculatorState | null };
    const calc = latest.calculatorState ?? null;
    if (!calc) return [];
    const main = (calc.main ?? []).map((p) => ({ ...p, kind: "main" as const }));
    const extras = (calc.extra ?? [])
      .filter((p) => p.selected)
      .map((p) => ({ ...p, kind: "extra" as const }));
    return [...main, ...extras];
  }, [proposalsList.data]);

  const selectedProductCards = useMemo<
    Array<{
      stripe_product_id: string;
      qty: number;
      kind: "main" | "extra";
      reason?: string;
      source?: "manual" | "ai";
      discount_cents?: number;
    }>
  >(() => {
    const list =
      (opp.data as unknown as {
        selectedProducts?:
          | Array<{
              stripe_product_id: string;
              qty: number;
              kind: "main" | "extra";
              reason?: string;
              source?: "manual" | "ai";
              discount_cents?: number;
            }>
          | null;
      } | undefined)?.selectedProducts ?? [];
    return list;
  }, [opp.data]);

  const productsTabCount = proposedProducts.length || selectedProductCards.length;

  const productCategorySummary = useMemo(() => {
    const list: Array<{ stripe_product_id: string }> =
      proposedProducts.length > 0
        ? proposedProducts.map((p) => ({ stripe_product_id: p.stripe_product_id }))
        : selectedProductCards.map((p) => ({ stripe_product_id: p.stripe_product_id }));
    const counts = { seats: 0, core: 0, hub: 0, onboarding: 0, other: 0 };
    for (const p of list) {
      const sub = productsByStripeId.get(p.stripe_product_id)?.subCategory ?? null;
      if (sub === "seats") counts.seats++;
      else if (sub === "core") counts.core++;
      else if (sub === "hub") counts.hub++;
      else if (sub === "onboarding") counts.onboarding++;
      else counts.other++;
    }
    const errors: string[] = [];
    if (counts.core > 1)
      errors.push(`Only one STORAGE PLAN product is allowed (found ${counts.core}).`);
    return { counts, errors, hasItems: list.length > 0 };
  }, [proposedProducts, selectedProductCards, productsByStripeId]);

  const update = trpc.opportunities.update.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: id! });
      utils.opportunities.list.invalidate();
      setEditingField(null);
    },
    onError: () => toast.error("Failed to save"),
  });

  const updateStatus = trpc.opportunities.updateStatus.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: id! });
      utils.opportunities.list.invalidate();
    },
    onError: () => toast.error("Failed to update status"),
  });

  const updateClient = trpc.clients.update.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: id! });
      utils.opportunities.list.invalidate();
    },
    onError: () => toast.error("Failed to update client type"),
  });

  const deleteOpp = trpc.opportunities.delete.useMutation({
    onSuccess: () => {
      toast.success("Opportunity deleted");
      navigate("/");
    },
    onError: () => toast.error("Failed to delete"),
  });

  const createProposal = trpc.proposals.create.useMutation({
    onSuccess: (proposal) => {
      toast.success("Proposal created");
      setTemplatePickerOpen(false);
      navigate(`/proposals/${proposal.id}`);
    },
    onError: (e) => {
      toast.error(e.message ?? "Failed to create proposal");
      setCreatingProposal(false);
    },
  });

  // Skeleton proposal auto-created when the user opens the products tab
  // without an existing proposal — backs the Quote builder so toggling
  // extras and perks can persist. No AI work runs (skipAi), no toast, no
  // navigation. Prime the cache so targetProposal is available immediately
  // and the QuoteBuilder reseed doesn't race the refetch.
  const autoCreateProposal = trpc.proposals.create.useMutation({
    onSuccess: (proposal) => {
      if (!id) return;
      utils.proposals.list.setData(
        { opportunityId: id },
        (old) => (old ? [...old, proposal] : [proposal])
      );
      utils.proposals.list.invalidate({ opportunityId: id });
    },
  });
  const autoCreatedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id) return;
    if (autoCreatedForRef.current === id) return;
    if (activeTab !== "products") return;
    if (proposalsList.isLoading || opp.isLoading) return;
    if ((proposalsList.data?.length ?? 0) > 0) return;
    if (selectedProductCards.length === 0) return;
    if (autoCreateProposal.isPending) return;
    autoCreatedForRef.current = id;
    autoCreateProposal.mutate({ opportunityId: id, skipAi: true });
  }, [
    id,
    activeTab,
    proposalsList.isLoading,
    proposalsList.data,
    opp.isLoading,
    selectedProductCards.length,
    autoCreateProposal,
  ]);

  // Shared success/error handling for the brief-generation endpoint. Both the
  // "Regenerate both" flow and the brief-only button hit the same procedure, but
  // each gets its own useMutation instance below so their loading state stays
  // independent (clicking one must not light up the other's spinner).
  const briefMutationHandlers = {
    onSuccess: (data: unknown) => {
      const d = data as { bodyRefreshed?: boolean; bodyRefreshFailed?: boolean };
      if (d.bodyRefreshFailed) {
        toast.warning(
          "Brief generated, but refreshing the public page failed — open the proposal and click Regenerate to retry."
        );
      } else if (d.bodyRefreshed) {
        toast.success("Brief generated and public page refreshed.");
      } else {
        toast.success("Brief generated from notes.");
      }
      utils.opportunities.get.invalidate({ id: id! });
      // The server also refreshes the active proposal's body, so pull the
      // updated proposal into the cache for the landing-page preview/links.
      utils.proposals.list.invalidate({ opportunityId: id! });
    },
    onError: (e: { message?: string }) =>
      toast.error(e.message ?? "Failed to generate brief"),
  };

  // Used by the combined "Regenerate both" flow (alongside generatePicture).
  const generateBrief =
    trpc.opportunities.generateBriefFromNotes.useMutation(briefMutationHandlers);
  // Dedicated instance for the brief-only "Regenerate brief" button so its
  // loading state never bleeds into the "Regenerate both" button.
  const regenerateBriefOnly =
    trpc.opportunities.generateBriefFromNotes.useMutation(briefMutationHandlers);

  // The dialog is closed and a loading toast is shown per-call from the
  // Regenerate button's onClick, so the mutation only handles cache
  // invalidation here — the toast lifecycle is managed at the call site
  // via the per-call onSuccess/onError options.
  const rerenderProposal = trpc.proposals.rerender.useMutation({
    onSettled: () => {
      utils.proposals.list.invalidate({ opportunityId: id! });
    },
  });
  const regeneratingProposalId = rerenderProposal.isPending
    ? (rerenderProposal.variables?.id ?? null)
    : null;

  // The authoritative visibleSections array saved by both the Save (visibility
  // only) and Regenerate (AI re-run) actions. Order mirrors the public proposal
  // render order; showSection() reads membership, so a toggled-off key is simply
  // omitted here and the public page hides it. Keep the key set in sync with the
  // toggles rendered in the regenerate modal.
  const collectRerenderVisibleSections = () =>
    [
      "hero",
      "dashboard",
      "integrations",
      "ai_agent",
      "feedback_loop",
      "needs",
      "pain_points",
      "scope",
      "outcomes",
      "timeline",
      "why_us",
      "case_studies",
      "next_steps",
      "recommended",
      "calculator",
      "open_questions",
      "footer",
      "additional_services",
    ].filter((k) => rerenderSections[k]);

  const addNote = trpc.opportunities.addNote.useMutation({
    onSuccess: () => {
      setNewNote("");
      utils.opportunities.notes.invalidate({ opportunityId: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to save note"),
  });

  const editNote = trpc.opportunities.editNote.useMutation({
    onSuccess: () => {
      utils.opportunities.notes.invalidate({ opportunityId: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to update note"),
  });

  const deleteNote = trpc.opportunities.deleteNote.useMutation({
    onSuccess: () => {
      utils.opportunities.notes.invalidate({ opportunityId: id! });
    },
    onError: () => toast.error("Failed to delete note"),
  });

  const generatePicture = trpc.opportunities.generatePicture.useMutation({
    onSuccess: () => {
      toast.success("Picture generated");
      utils.opportunities.get.invalidate({ id: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to generate picture"),
  });

  const generateCallPlan = trpc.opportunities.generateCallPlan.useMutation({
    onSuccess: () => {
      toast.success("Call plan generated");
      utils.opportunities.get.invalidate({ id: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to generate call plan"),
  });

  const runCallPlanGenerate = (instruction?: string) => {
    if (!id) return;
    generateCallPlan.mutate({ id, instruction: instruction?.trim() || undefined });
  };

  const runOpportunityAiGenerate = (instruction?: string) => {
    if (!id) return;
    const trimmed = instruction?.trim() || undefined;
    generatePicture.mutate({ id, instruction: trimmed });
    // generateBriefFromNotes also refreshes the active proposal's body
    // server-side so the public /p/<slug> page stays in sync with the new
    // brief — no separate client-side body regen is needed.
    generateBrief.mutate({ id, instruction: trimmed });
  };

  const trackingEventsQuery = trpc.opportunities.trackingEvents.useQuery(
    { opportunityId: id! },
    { enabled: !!id }
  );

  if (opp.isLoading) {
    return (
      <div className="mx-auto max-w-screen-xl space-y-4 px-4 py-6 sm:px-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!opp.data) {
    return (
      <div className="mx-auto max-w-screen-xl px-4 py-6 sm:px-6">
        <Button variant="ghost" asChild>
          <Link to="/"><ArrowLeft className="h-4 w-4" />Back</Link>
        </Button>
        <p className="mt-4 text-muted-foreground">Opportunity not found.</p>
      </div>
    );
  }

  const o = opp.data;
  const brief = o.brief as Brief | null;

  const startEdit = (field: string, value: string | null) => {
    setEditingField(field);
    setFieldValues({ ...fieldValues, [field]: value ?? "" });
  };

  const saveField = (field: string) => {
    const val = fieldValues[field];
    update.mutate({ id: id!, patch: { [field]: val || null } });
  };

  const statusInfo = STATUS_LABELS[o.status] ?? { label: o.status, color: "" };

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-6 px-4 py-6 sm:px-6">
      {/* ── TOP BAR ── */}
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-3">
          <Link to="/"><ArrowLeft className="h-4 w-4" />Opportunities</Link>
        </Button>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          {editingField === "name" ? (
            <div className="flex flex-1 items-center gap-2">
              <Input
                autoFocus
                value={fieldValues["name"] ?? o.name}
                onChange={(e) => setFieldValues({ ...fieldValues, name: e.target.value })}
                className="text-xl font-semibold"
                onKeyDown={(e) => e.key === "Enter" && saveField("name")}
              />
              <Button size="icon" variant="ghost" onClick={() => saveField("name")}><Check className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
            </div>
          ) : (
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{o.name}</h1>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground"
                onClick={() => startEdit("name", o.name)}
                aria-label="Edit opportunity name"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              {(() => {
                const proposals = (proposalsList.data ?? []) as Array<{
                  publicToken: string;
                  status: string;
                }>;
                const target =
                  [...proposals].reverse().find((p) => p.status !== "accepted") ??
                  [...proposals].reverse()[0] ??
                  null;
                return target ? <PublicUrlBadge slug={o.publicSlug} /> : null;
              })()}
              <Select
                value={(o.language ?? "lt") as "lt" | "en"}
                onValueChange={(v) =>
                  update.mutate({ id: id!, patch: { language: v as "lt" | "en" } })
                }
              >
                <SelectTrigger
                  className="h-7 w-auto gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium uppercase text-muted-foreground shadow-none hover:bg-muted hover:text-foreground focus:ring-0"
                  title="Default language — used for AI content, email templates, etc."
                >
                  <Languages className="h-3 w-3" />
                  {(o.language ?? "lt").toUpperCase()}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lt">LT — Lithuanian</SelectItem>
                  <SelectItem value="en">EN — English</SelectItem>
                </SelectContent>
              </Select>
              <OpportunityOwnerSelect
                ownerId={o.ownerId ?? null}
                onChange={(ownerId) => update.mutate({ id: id!, patch: { ownerId } })}
                disabled={update.isPending}
              />
              {(() => {
                const proposals = (proposalsList.data ?? []) as Array<{
                  id: string;
                  status: string;
                }>;
                const target =
                  [...proposals].reverse().find((p) => p.status !== "accepted") ??
                  [...proposals].reverse()[0] ??
                  null;
                const blockedNoBrief = !target && !brief;
                const isRegenerating = rerenderProposal.isPending;
                const disabled = proposalsList.isLoading || blockedNoBrief || isRegenerating;
                const tooltip = isRegenerating
                  ? "Regenerating the proposal body — this can take up to a minute."
                  : blockedNoBrief
                  ? "Generate a brief first — the proposal needs the AI brief as its input."
                  : target
                    ? "Choose language and section visibility for this proposal"
                    : "Generate a proposal for this opportunity";
                return (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-flex">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1.5 rounded-full px-3 text-xs"
                            disabled={disabled}
                            onClick={() => {
                              if (target) {
                                setRerenderProposalId(target.id);
                              } else {
                                setTemplatePickerOpen(true);
                              }
                            }}
                          >
                            {isRegenerating ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            {isRegenerating
                              ? "Regenerating…"
                              : target
                                ? "Regenerate"
                                : "Generate"}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="normal-case tracking-normal">
                        {tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })()}
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 rounded-full px-3 text-xs"
                onClick={() => {
                  setReplyContext(null);
                  setMessageModalOpen(true);
                }}
                title="Write a message to the client"
              >
                <Mail className="h-3 w-3" />
                Message client
              </Button>
              <OpportunityTagsInline opportunityId={o.id} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <NextActionInline opportunityId={o.id} nextActionAt={o.nextActionAt} />
            {o.client && (
              <ClientTypeSelect
                value={o.client.industryType ?? null}
                onChange={(v) =>
                  updateClient.mutate({
                    id: o.client!.id,
                    patch: { industryType: v },
                  })
                }
                disabled={updateClient.isPending}
              />
            )}
            <Select
              value={o.status}
              onValueChange={(v) => updateStatus.mutate({ id: id!, status: v as typeof o.status })}
            >
              <SelectTrigger className="h-8 w-auto gap-1 border-0 bg-transparent p-0 shadow-none focus:ring-0">
                <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", statusInfo.color)}>
                  {statusInfo.label}
                </span>
              </SelectTrigger>
              <SelectContent>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", STATUS_LABELS[s].color)}>
                      {STATUS_LABELS[s].label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  aria-label="Delete opportunity"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete opportunity?</AlertDialogTitle>
                  <AlertDialogDescription>
                    All related proposals will also be deleted. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => deleteOpp.mutate({ id: id! })}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

      </div>

      <div className="min-w-0 space-y-5">
        {/* Status pipeline */}
        <OpportunityStatusBar
          opportunityId={o.id}
          currentStatus={o.status}
          createdAt={o.createdAt}
          briefGeneratedAt={o.briefGeneratedAt ?? null}
          callAt={o.callAt ?? null}
          selectedProducts={o.selectedProducts ?? null}
          selectedProductsAt={o.selectedProductsAt ?? null}
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsTrigger value="notes" className="gap-1.5">
              Notes
              {(notesQuery.data?.length ?? 0) > 0 && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground">
                  {notesQuery.data!.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="call_plan" className="gap-1.5">
              Call plan
              {o.callPlan && <CircleCheck className="h-3.5 w-3.5 text-emerald-600" />}
            </TabsTrigger>
            <TabsTrigger value="opportunity" className="gap-1.5">
              Opportunity & Brief
              {o.opportunityPicture && brief && (
                <CircleCheck className="h-3.5 w-3.5 text-emerald-600" />
              )}
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-1.5">
              Proposal
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground">
                {productsTabCount}
              </span>
            </TabsTrigger>
            <TabsTrigger value="client">Client</TabsTrigger>
            <TabsTrigger value="documents" className="gap-1.5">
              Documents
              {(documentsQuery.data?.length ?? 0) > 0 && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground">
                  {documentsQuery.data!.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* ── Notes tab ── */}
          <TabsContent value="notes" className="space-y-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  <StickyNote className="h-3.5 w-3.5" />
                  Notes
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => {
                    setReplyContext(null);
                    setMessageModalOpen(true);
                  }}
                  title="Write an email to the client"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email client
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        const trimmed = newNote.trim();
                        if (trimmed) addNote.mutate({ opportunityId: id!, content: trimmed });
                      }
                    }}
                    placeholder="Add a new note... (Ctrl+Enter to save)"
                    className="min-h-[80px] text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => {
                        const trimmed = newNote.trim();
                        if (trimmed) addNote.mutate({ opportunityId: id!, content: trimmed });
                      }}
                      disabled={addNote.isPending || !newNote.trim()}
                      className="gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add note
                    </Button>
                  </div>
                </div>

                {notesQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : (notesQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No notes yet. Add the first — everything stays in chronological order.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {notesQuery.data!.map((n) => (
                      <NoteRow
                        key={n.id}
                        note={n}
                        onDelete={() => deleteNote.mutate({ noteId: n.id })}
                        deleting={deleteNote.isPending}
                        onSave={(content) => editNote.mutate({ noteId: n.id, content })}
                        saving={editNote.isPending}
                        onReply={(toEmail, subject) => {
                          setReplyContext({ toEmail, subject });
                          setMessageModalOpen(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Estimate */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Estimate</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                <EditableField label="Plan" field="estimatedPlan" value={o.estimatedPlan} editing={editingField} fieldValues={fieldValues} onStart={startEdit} onSave={saveField} onCancel={() => setEditingField(null)} onChange={(f, v) => setFieldValues({ ...fieldValues, [f]: v })} />
                <EditableField label="Seats" field="estimatedSeats" value={o.estimatedSeats?.toString()} editing={editingField} fieldValues={fieldValues} onStart={startEdit} onSave={(f) => update.mutate({ id: id!, patch: { estimatedSeats: Number(fieldValues[f]) || null } }, { onSuccess: () => { utils.opportunities.get.invalidate({ id: id! }); setEditingField(null); } })} onCancel={() => setEditingField(null)} onChange={(f, v) => setFieldValues({ ...fieldValues, [f]: v })} />
                <EditableField label="MRR (EUR)" field="estimatedMrrEur" value={o.estimatedMrrEur != null ? `€${o.estimatedMrrEur}/mo` : null} editing={editingField} fieldValues={fieldValues} onStart={startEdit} onSave={(f) => update.mutate({ id: id!, patch: { estimatedMrrEur: Number(fieldValues[f]) || null } }, { onSuccess: () => { utils.opportunities.get.invalidate({ id: id! }); setEditingField(null); } })} onCancel={() => setEditingField(null)} onChange={(f, v) => setFieldValues({ ...fieldValues, [f]: v })} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Call plan tab ── */}
          <TabsContent value="call_plan" className="space-y-5">
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                <Phone className="h-3.5 w-3.5" style={{ color: "#723FF4" }} />
                Call plan
                <span className="ml-2 text-[10px] font-semibold tracking-wider text-muted-foreground">
                  {(o.language ?? "lt").toUpperCase()}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (o.callPlan) {
                    setCallPlanInstruction("");
                    setCallPlanRegenOpen(true);
                  } else {
                    runCallPlanGenerate();
                  }
                }}
                disabled={generateCallPlan.isPending}
                className="h-7 gap-1.5 text-xs"
              >
                {generateCallPlan.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wand2 className="h-3 w-3" />
                )}
                {o.callPlan ? "Regenerate" : "Generate with AI"}
              </Button>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-yellow-400 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800 dark:border-yellow-600 dark:bg-yellow-950 dark:text-yellow-300">
              <span>⚠️</span>
              <span>DONT FORGET TO Turn on Soniox for record</span>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  <Phone className="h-3.5 w-3.5" />
                  Call plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generateCallPlan.isPending && !o.callPlan ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-10/12" />
                    <Skeleton className="h-4 w-9/12" />
                  </div>
                ) : o.callPlan ? (
                  <Markdown content={o.callPlan} className="text-sm leading-relaxed" />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Click "Generate with AI" — the AI will build a tactical call plan from the brief, notes and client profile: objective, opening, questions to ask, pain points to focus on, and likely objections. It's also generated automatically when you set the CALL PENDING tag.
                  </p>
                )}
                {o.callPlanGeneratedAt && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Updated {new Date(o.callPlanGeneratedAt).toLocaleString("en-US")}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Opportunity & Brief tab ── */}
          <TabsContent value="opportunity" className="space-y-5">
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                <Sparkles className="h-3.5 w-3.5" style={{ color: "#723FF4" }} />
                Opportunity & Brief
                <span className="ml-2 text-[10px] font-semibold tracking-wider text-muted-foreground">
                  {(o.language ?? "lt").toUpperCase()}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (o.opportunityPicture && brief) {
                    setRegenAiInstruction("");
                    setRegenAiOpen(true);
                  } else {
                    runOpportunityAiGenerate();
                  }
                }}
                disabled={generatePicture.isPending || generateBrief.isPending}
                className="h-7 gap-1.5 text-xs"
              >
                {generatePicture.isPending || generateBrief.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wand2 className="h-3 w-3" />
                )}
                {o.opportunityPicture && brief ? "Regenerate both" : "Generate with AI"}
              </Button>
            </div>

            {/* Opportunity block */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Client and opportunity picture
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatePicture.isPending && !o.opportunityPicture ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-10/12" />
                    <Skeleton className="h-4 w-9/12" />
                  </div>
                ) : o.opportunityPicture ? (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {o.opportunityPicture}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Click "Generate with AI" — the AI will write a detailed client and opportunity picture from the client profile, notes, and brief.
                  </p>
                )}
                {o.opportunityPictureGeneratedAt && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Updated {new Date(o.opportunityPictureGeneratedAt).toLocaleString("en-US")}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Brief block */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Brief
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => regenerateBriefOnly.mutate({ id: id! })}
                  disabled={regenerateBriefOnly.isPending}
                  className="h-7 gap-1.5 text-xs"
                >
                  {regenerateBriefOnly.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Wand2 className="h-3 w-3" />
                  )}
                  {brief ? "Regenerate brief" : "Generate brief"}
                </Button>
              </CardHeader>
              <CardContent>
                {(generateBrief.isPending || regenerateBriefOnly.isPending) && !brief ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-10/12" />
                    <Skeleton className="h-4 w-9/12" />
                  </div>
                ) : brief ? (
                  <BriefView brief={brief} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Click "Generate brief" — the AI will distill the opportunity notes into a structured brief that powers proposal generation.
                  </p>
                )}
                {o.briefGeneratedAt && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Updated {new Date(o.briefGeneratedAt).toLocaleString("en-US")}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Products tab — full quote builder ── */}
          <TabsContent value="products" className="space-y-5">
            {trackingEventsQuery.data?.some((e) => e.eventType === "pdf_downloaded") && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>Client has generated a PDF.</strong> Be aware that products and services may have changed since the PDF was downloaded.
                </span>
              </div>
            )}
            <OpportunityQuoteBuilder
              opportunityId={id!}
              industryType={o.client?.industryType ?? null}
              language={(o.language ?? "lt") as "lt" | "en"}
              proposals={(proposalsList.data ?? []) as ProposalRowForQuote[]}
              proposalsLoading={proposalsList.isLoading || autoCreateProposal.isPending}
              selectedProductCards={selectedProductCards}
              productsByStripeId={productsByStripeId}
              productCategorySummary={productCategorySummary}
              onPersistAdd={(stripeProductId, kind) =>
                addSelectedProduct.mutate({ id: id!, stripeProductId, kind })
              }
              onPersistRemove={(stripeProductId, kind) =>
                deleteSelectedProduct.mutate({
                  id: id!,
                  stripeProductId,
                  kind,
                })
              }
              persistMutating={
                addSelectedProduct.isPending ||
                deleteSelectedProduct.isPending
              }
            />

            <OpportunityPerksCatalog
              opportunityId={id!}
              language={(o.language ?? "lt") as "lt" | "en"}
              proposals={(proposalsList.data ?? []) as ProposalRowForQuote[]}
            />
          </TabsContent>

          {/* ── History tab — tracking events ── */}
          <TabsContent value="history" className="space-y-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  <Eye className="h-3.5 w-3.5" />
                  Tracking events
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trackingEventsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-3/4" />
                  </div>
                ) : !trackingEventsQuery.data || trackingEventsQuery.data.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No events yet. The quote landing page hasn't been opened. Visits from your team's IPs are excluded via the Tracking settings.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {trackingEventsQuery.data.map((event) => (
                      <TrackingEventRow key={event.id} event={event} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Klientas tab ── */}
          <TabsContent value="client" className="space-y-5">
            <ClientProfileCard
              o={o}
              brief={brief}
              statusInfo={statusInfo}
              editingField={editingField}
              fieldValues={fieldValues}
              startEdit={startEdit}
              saveField={saveField}
              onCancel={() => setEditingField(null)}
              onChange={(f, v) => setFieldValues({ ...fieldValues, [f]: v })}
            />

            {o.clientId ? (
              <>
                <ClientCompanyDetails clientId={o.clientId} />
                <ClientAccountingSystemsCard clientId={o.clientId} />
                <ClientContacts clientId={o.clientId} />
              </>
            ) : (
              <Card>
                <CardContent className="py-4 text-sm text-muted-foreground">
                  Link a client to this opportunity to see company details and accounting systems.
                </CardContent>
              </Card>
            )}

            {proposalsList.data && proposalsList.data.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Proposals</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {proposalsList.data.map((p: { id: string; status: string; publicToken: string; createdAt?: Date | string }) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Proposal</span>
                        <ProposalStatusBadge status={p.status} />
                        {regeneratingProposalId === p.id ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Regenerating…
                          </span>
                        ) : justRegeneratedId === p.id ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                            <Check className="h-3 w-3" />
                            Updated
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/proposals/${p.id}`}>Edit</Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href={`/p/${o.publicSlug}`} target="_blank" rel="noopener noreferrer">
                            <Send className="h-3 w-3" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Documents tab ── */}
          <TabsContent value="documents" className="space-y-5">
            <OpportunityDocuments opportunityId={o.id} />
            <OpportunitySigning opportunityId={o.id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Regenerate call plan modal */}
      <Dialog
        open={callPlanRegenOpen}
        onOpenChange={(open) => {
          if (!open) setCallPlanRegenOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate call plan</DialogTitle>
            <DialogDescription>
              Tell the AI what to change. Leave blank to regenerate from the
              latest notes, brief and client profile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label
              htmlFor="call-plan-instruction"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              What should change?
            </Label>
            <Textarea
              id="call-plan-instruction"
              value={callPlanInstruction}
              onChange={(e) => setCallPlanInstruction(e.target.value)}
              placeholder="e.g. Focus on overcoming their concern about switching from Rivilė and keep it short."
              rows={4}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallPlanRegenOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                runCallPlanGenerate(callPlanInstruction);
                setCallPlanRegenOpen(false);
              }}
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate opportunity & brief modal */}
      <Dialog
        open={regenAiOpen}
        onOpenChange={(open) => {
          if (!open) setRegenAiOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate opportunity & brief</DialogTitle>
            <DialogDescription>
              Tell the AI what to change. Leave blank to regenerate from the
              latest notes and client profile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label
              htmlFor="regen-ai-instruction"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              What should change?
            </Label>
            <Textarea
              id="regen-ai-instruction"
              value={regenAiInstruction}
              onChange={(e) => setRegenAiInstruction(e.target.value)}
              placeholder="e.g. Emphasize the migration from their old accounting system and shorten the brief."
              rows={4}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenAiOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                runOpportunityAiGenerate(regenAiInstruction);
                setRegenAiOpen(false);
              }}
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate-proposal modal */}
      <Dialog
        open={templatePickerOpen}
        onOpenChange={(o) => {
          setTemplatePickerOpen(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate proposal</DialogTitle>
            <DialogDescription>Pick which sections appear on the public proposal page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Language
            </Label>
            <div className="inline-flex rounded-md border bg-background p-0.5 text-sm">
              {(["lt", "en"] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setGenerateLanguage(lang)}
                  className={
                    "px-3 py-1 rounded-sm font-medium transition-colors " +
                    (generateLanguage === lang
                      ? "bg-zinc-900 text-white"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {lang === "lt" ? "LT — Lithuanian" : "EN — English"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sections to include
            </Label>
            <div className="max-h-[320px] divide-y overflow-y-auto rounded-md border">
              {([
                // Order mirrors the public proposal page top to bottom (hero →
                // brand sections → body sections → calculator → open questions →
                // feedback → footer), and the labels match the names shown there.
                // Keep this in sync with the render order in public-proposal.tsx
                // (BRAND_SECTION_IDS, then BODY_SECTION_IDS).
                { key: "hero", label: "Hero", desc: "Cover with headline and client info" },
                { key: "dashboard", label: "Product view", desc: "Product screenshot + caption columns" },
                { key: "integrations", label: "Integrations", desc: "Logo grid of supported systems" },
                { key: "ai_agent", label: "AI agent", desc: "What the agent does for the client (3 cards)" },
                { key: "feedback_loop", label: "Feedback loop", desc: "Customer feedback portal mockup" },
                { key: "needs", label: "Needs", desc: "What the client is asking for (only shows when there's content)" },
                { key: "pain_points", label: "Challenges", desc: "Client problems the proposal solves" },
                { key: "scope", label: "Scope of work", desc: "What we'll do for the client" },
                { key: "outcomes", label: "What you'll achieve", desc: "Measurable benefits for the client" },
                { key: "timeline", label: "Implementation plan", desc: "Phases with rough timing" },
                { key: "why_us", label: "Why Noium", desc: "Differentiators tailored to this client" },
                { key: "case_studies", label: "Case studies", desc: "Short proof-point quotes (only shows when there's content)" },
                { key: "next_steps", label: "Next steps", desc: "What happens after they accept (only shows when there's content)" },
                { key: "recommended", label: "Recommended membership", desc: "Plan card shown inside the pricing calculator" },
                { key: "calculator", label: "Pricing calculator", desc: "Quantities, totals, accept CTA" },
                { key: "open_questions", label: "Open questions", desc: "Unresolved questions for the client to answer (only shows when the brief has open questions)" },
                { key: "feedbackForm", label: "Feedback form", desc: "\"Tell us what you think\" form" },
                { key: "footer", label: "Footer", desc: "Closing card with preparer details" },
                // Optional add-on section, off on the public page by default.
                { key: "additional_services", label: "Additional services", desc: "Optional add-on modules — all toggleable by the client" },
              ] as const).map(({ key, label, desc }) => (
                <div key={key} className="flex items-start justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                  <Switch
                    checked={generateSections[key] ?? true}
                    onCheckedChange={(v) =>
                      setGenerateSections((s) => ({ ...s, [key]: v }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplatePickerOpen(false)}>Cancel</Button>
            <Button
              disabled={createProposal.isPending}
              onClick={() => {
                setCreatingProposal(true);
                const visibleSections = [
                  "hero",
                  "dashboard",
                  "integrations",
                  "ai_agent",
                  "feedback_loop",
                  "pain_points",
                  "scope",
                  "outcomes",
                  "timeline",
                  "why_us",
                  "recommended",
                  "calculator",
                  "open_questions",
                  "footer",
                  "needs",
                  "case_studies",
                  "next_steps",
                  "additional_services",
                ].filter((k) => generateSections[k]);
                createProposal.mutate({
                  opportunityId: id!,
                  visibleSections,
                  landingFeatures: { feedbackForm: generateSections.feedbackForm },
                  language: generateLanguage,
                });
              }}
            >
              {createProposal.isPending ? "Creating..." : "Generate proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate (change template + sections) modal */}
      <Dialog
        open={rerenderProposalId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setRerenderProposalId(null);
                }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Proposal sections</DialogTitle>
            <DialogDescription>
              Choose which sections are visible on the public proposal. Use{" "}
              <strong>Save</strong> to apply visibility instantly (the content is
              already generated), or <strong>Regenerate</strong> to rewrite the
              body with the AI — only needed when the brief changed or you switch
              language.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Language
            </Label>
            <div className="inline-flex rounded-md border bg-background p-0.5 text-sm">
              {(["lt", "en"] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setRerenderLanguage(lang)}
                  className={
                    "px-3 py-1 rounded-sm font-medium transition-colors " +
                    (rerenderLanguage === lang
                      ? "bg-zinc-900 text-white"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {lang === "lt" ? "LT — Lithuanian" : "EN — English"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sections shown on the proposal
            </Label>
            <div className="max-h-[320px] divide-y overflow-y-auto rounded-md border">
              {([
                // Order mirrors the public proposal page top to bottom (hero →
                // brand sections → body sections → calculator → open questions →
                // feedback → footer), and the labels match the names shown there.
                // Keep this in sync with the render order in public-proposal.tsx
                // (BRAND_SECTION_IDS, then BODY_SECTION_IDS).
                { key: "hero", label: "Hero", desc: "Cover with headline and client info" },
                { key: "dashboard", label: "Product view", desc: "Product screenshot + caption columns" },
                { key: "integrations", label: "Integrations", desc: "Logo grid of supported systems" },
                { key: "ai_agent", label: "AI agent", desc: "What the agent does for the client (3 cards)" },
                { key: "feedback_loop", label: "Feedback loop", desc: "Customer feedback portal mockup" },
                { key: "needs", label: "Needs", desc: "What the client is asking for (only shows when there's content)" },
                { key: "pain_points", label: "Challenges", desc: "Client problems the proposal solves" },
                { key: "scope", label: "Scope of work", desc: "What we'll do for the client" },
                { key: "outcomes", label: "What you'll achieve", desc: "Measurable benefits for the client" },
                { key: "timeline", label: "Implementation plan", desc: "Phases with rough timing" },
                { key: "why_us", label: "Why Noium", desc: "Differentiators tailored to this client" },
                { key: "case_studies", label: "Case studies", desc: "Short proof-point quotes (only shows when there's content)" },
                { key: "next_steps", label: "Next steps", desc: "What happens after they accept (only shows when there's content)" },
                { key: "recommended", label: "Recommended membership", desc: "Plan card shown inside the pricing calculator" },
                { key: "calculator", label: "Pricing calculator", desc: "Quantities, totals, accept CTA" },
                { key: "open_questions", label: "Open questions", desc: "Unresolved questions for the client to answer (only shows when the brief has open questions)" },
                { key: "feedbackForm", label: "Feedback form", desc: "\"Tell us what you think\" form" },
                { key: "footer", label: "Footer", desc: "Closing card with preparer details" },
                // Optional add-on section, off on the public page by default.
                { key: "additional_services", label: "Additional services", desc: "Optional add-on modules — all toggleable by the client" },
              ] as const).map(({ key, label, desc }) => (
                <div key={key} className="flex items-start justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground">{desc}</div>
                  </div>
                  <Switch
                    checked={rerenderSections[key] ?? true}
                    onCheckedChange={(v) =>
                      setRerenderSections((s) => ({ ...s, [key]: v }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRerenderProposalId(null);
                        }}
            >
              Cancel
            </Button>
            {/* Save visibility only — the body text is already generated, so
                showing/hiding sections is just a write of visibleSections. This
                skips the 30–60s AI regeneration entirely (the rerender procedure
                only calls Claude when regenerateBody / a language change is
                passed, neither of which we send here). */}
            <Button
              variant="outline"
              disabled={!rerenderProposalId || rerenderProposal.isPending}
              onClick={() => {
                const visibleSections = collectRerenderVisibleSections();
                const proposalId = rerenderProposalId!;
                setRerenderProposalId(null);
                const toastId = toast.loading("Saving visibility…");
                rerenderProposal.mutate(
                  {
                    id: proposalId,
                    visibleSections,
                    landingFeatures: { feedbackForm: rerenderSections.feedbackForm },
                    // No language / regenerateBody → server saves the toggles
                    // without touching the AI-generated body.
                  },
                  {
                    onSuccess: () => {
                      toast.success("Visibility saved", { id: toastId });
                      setJustRegeneratedId(proposalId);
                      if (justRegeneratedTimer.current) {
                        clearTimeout(justRegeneratedTimer.current);
                      }
                      justRegeneratedTimer.current = setTimeout(
                        () => setJustRegeneratedId(null),
                        6000
                      );
                    },
                    onError: (e) => {
                      toast.error(e.message ?? "Failed to save", { id: toastId });
                    },
                  }
                );
              }}
            >
              Save
            </Button>
            <Button
              disabled={!rerenderProposalId}
              onClick={() => {
                const visibleSections = collectRerenderVisibleSections();
                const proposalId = rerenderProposalId!;
                // Close the dialog immediately — body regeneration can take
                // 30–60s when the AI has to populate sections from the brief,
                // and leaving "Regenerating..." up that long looks stuck.
                setRerenderProposalId(null);
                const toastId = toast.loading("Regenerating proposal…");
                rerenderProposal.mutate(
                  {
                    id: proposalId,
                    visibleSections,
                    landingFeatures: { feedbackForm: rerenderSections.feedbackForm },
                    language: rerenderLanguage,
                    regenerateBody: true,
                  },
                  {
                    onSuccess: () => {
                      toast.success("Proposal regenerated", { id: toastId });
                      setJustRegeneratedId(proposalId);
                      if (justRegeneratedTimer.current) {
                        clearTimeout(justRegeneratedTimer.current);
                      }
                      justRegeneratedTimer.current = setTimeout(
                        () => setJustRegeneratedId(null),
                        6000
                      );
                    },
                    onError: (e) => {
                      toast.error(e.message ?? "Failed to regenerate", { id: toastId });
                    },
                  }
                );
              }}
            >
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MessageClientDialog
        open={messageModalOpen}
        onOpenChange={(v) => {
          setMessageModalOpen(v);
          if (!v) setReplyContext(null);
        }}
        replyContext={replyContext}
        opportunityId={o.id}
        opportunityName={o.name}
        clientId={o.clientId ?? null}
        defaultEmail={o.contactEmail}
        contactName={joinContactName(o.contactFirstName, o.contactLastName)}
        contactFirstName={o.contactFirstName}
        contactLastName={o.contactLastName}
        contactReferenceName={o.contactReferenceName}
        clientCompany={o.client?.companyName ?? null}
        landingUrl={(() => {
          const proposals = (proposalsList.data ?? []) as Array<{
            publicToken: string;
            status: string;
          }>;
          const hasProposal = proposals.length > 0;
          return hasProposal && o.publicSlug
            ? `${window.location.origin}/p/${o.publicSlug}`
            : null;
        })()}
        language={(o.language ?? "lt") as "lt" | "en"}
        onSent={() => {
          setMessageModalOpen(false);
          setReplyContext(null);
          utils.opportunities.notes.invalidate({ opportunityId: id! });
        }}
      />
    </div>
  );
}

const BRIEF_CLIENT_KEYS = ["name", "company", "email", "phone", "industry", "size"] as const;

/** Re-nest top-level client identity fields under `brief.client`. */
function normalizeBriefShape(raw: Brief): Brief {
  if (!raw || typeof raw !== "object") return raw;
  const obj = raw as Record<string, unknown>;
  const hasFlatClient = BRIEF_CLIENT_KEYS.some((k) => obj[k] != null);
  if (!hasFlatClient) return raw;
  const next = { ...obj } as Record<string, unknown>;
  const client: Record<string, unknown> = { ...((obj.client as Record<string, unknown>) ?? {}) };
  for (const key of BRIEF_CLIENT_KEYS) {
    if (obj[key] != null && client[key] == null) client[key] = obj[key];
    delete next[key];
  }
  next.client = client;
  return next as Brief;
}

function BriefView({ brief: rawBrief }: { brief: Brief }) {
  // Some older / non-compliant briefs flatten the client identity fields onto
  // the top level instead of nesting them under `client`. Re-nest them here so
  // those briefs still render their client card instead of falling through to
  // "no recognizable fields".
  const brief = normalizeBriefShape(rawBrief);
  const hasClient = brief.client && Object.values(brief.client).some((v) => v != null && v !== "");
  const hasList = (xs?: string[]) => Array.isArray(xs) && xs.length > 0;
  const renderList = (xs: string[]) => (
    <ul className="ml-5 list-disc space-y-1 text-sm">
      {xs.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  );
  const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );

  const hasStructuredContent =
    hasClient ||
    !!brief.opportunity_summary ||
    !!brief.context ||
    hasList(brief.pain_points) ||
    hasList(brief.needs) ||
    !!brief.recommended_plan ||
    brief.estimated_seats != null ||
    brief.estimated_mrr_eur != null ||
    hasList(brief.recommended_addons) ||
    !!brief.positioning_strategy ||
    hasList(brief.open_questions) ||
    hasList(brief.next_steps);

  if (!hasStructuredContent) {
    const hasAnyKeys = brief && typeof brief === "object" && Object.keys(brief).length > 0;
    return (
      <div className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          {hasAnyKeys
            ? "The brief was generated but contains no recognizable fields. Try regenerating."
            : "The brief is empty. Try regenerating."}
        </p>
        {hasAnyKeys && (
          <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-3 text-[11px] leading-relaxed">
            {JSON.stringify(brief, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm leading-relaxed">
      {hasClient && (
        <Section label="Client">
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
            {brief.client?.company && (
              <div><span className="text-muted-foreground">Company: </span>{brief.client.company}</div>
            )}
            {brief.client?.name && (
              <div><span className="text-muted-foreground">Contact: </span>{brief.client.name}</div>
            )}
            {brief.client?.email && (
              <div><span className="text-muted-foreground">Email: </span>{brief.client.email}</div>
            )}
            {brief.client?.phone && (
              <div><span className="text-muted-foreground">Phone: </span>{brief.client.phone}</div>
            )}
            {brief.client?.industry && (
              <div><span className="text-muted-foreground">Industry: </span>{brief.client.industry}</div>
            )}
            {brief.client?.size && (
              <div><span className="text-muted-foreground">Size: </span>{brief.client.size}</div>
            )}
          </div>
        </Section>
      )}
      {brief.opportunity_summary && (
        <Section label="Opportunity summary">
          <p className="whitespace-pre-wrap">{brief.opportunity_summary}</p>
        </Section>
      )}
      {brief.context && (
        <Section label="Context">
          <p className="whitespace-pre-wrap">{brief.context}</p>
        </Section>
      )}
      {hasList(brief.pain_points) && (
        <Section label="Pain points">{renderList(brief.pain_points!)}</Section>
      )}
      {hasList(brief.needs) && (
        <Section label="Needs">{renderList(brief.needs!)}</Section>
      )}
      {(brief.recommended_plan || brief.estimated_seats != null || brief.estimated_mrr_eur != null) && (
        <Section label="Recommendation">
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-3">
            {brief.recommended_plan && (
              <div><span className="text-muted-foreground">Plan: </span>{brief.recommended_plan}</div>
            )}
            {brief.estimated_seats != null && (
              <div><span className="text-muted-foreground">Seats: </span>{brief.estimated_seats}</div>
            )}
            {brief.estimated_mrr_eur != null && (
              <div><span className="text-muted-foreground">MRR: </span>€{brief.estimated_mrr_eur}/mo</div>
            )}
          </div>
        </Section>
      )}
      {hasList(brief.recommended_addons) && (
        <Section label="Recommended add-ons">{renderList(brief.recommended_addons!)}</Section>
      )}
      {brief.positioning_strategy && (
        <Section label="Positioning strategy">
          <p className="whitespace-pre-wrap">{brief.positioning_strategy}</p>
        </Section>
      )}
      {hasList(brief.open_questions) && (
        <Section label="Open questions">{renderList(brief.open_questions!)}</Section>
      )}
      {hasList(brief.next_steps) && (
        <Section label="Next steps">{renderList(brief.next_steps!)}</Section>
      )}
    </div>
  );
}

function EditableField({
  label, field, value, editing, fieldValues, onStart, onSave, onCancel, onChange,
}: {
  label: string;
  field: string;
  value: string | null | undefined;
  editing: string | null;
  fieldValues: Record<string, string>;
  onStart: (f: string, v: string | null) => void;
  onSave: (f: string) => void;
  onCancel: () => void;
  onChange: (f: string, v: string) => void;
}) {
  if (editing === field) {
    return (
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={fieldValues[field] ?? ""}
            onChange={(e) => onChange(field, e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSave(field)}
            className="h-7 text-sm"
          />
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSave(field)}><Check className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onCancel}><X className="h-3 w-3" /></Button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="group flex cursor-pointer flex-col gap-0.5 rounded p-1 hover:bg-muted/50"
      onClick={() => onStart(field, value ?? null)}
    >
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1 text-sm">
        {value ?? <span className="text-muted-foreground">—</span>}
        <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </span>
    </div>
  );
}

type TrackingEvent = {
  id: string;
  proposalId: string;
  eventType: string;
  payload: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: Date | string;
};

function TrackingEventRow({ event }: { event: TrackingEvent }) {
  const formatted = new Date(event.createdAt).toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const labels: Record<string, { label: string; tone: string }> = {
    viewed: { label: "Landing page opened", tone: "bg-blue-100 text-blue-700" },
    sent: { label: "Email sent", tone: "bg-indigo-100 text-indigo-700" },
    accepted: { label: "Accepted by client", tone: "bg-emerald-100 text-emerald-700" },
    feedback_submitted: { label: "Client feedback", tone: "bg-amber-100 text-amber-700" },
    replied: { label: "Client replied", tone: "bg-amber-100 text-amber-700" },
    created: { label: "Proposal created", tone: "bg-zinc-100 text-zinc-700" },
    rerendered: { label: "Proposal re-rendered", tone: "bg-zinc-100 text-zinc-700" },
    email_delivered: { label: "Email delivered", tone: "bg-indigo-100 text-indigo-700" },
    email_opened: { label: "Email opened", tone: "bg-blue-100 text-blue-700" },
    email_unique_opened: { label: "Email opened", tone: "bg-blue-100 text-blue-700" },
    email_click: { label: "Email link clicked", tone: "bg-sky-100 text-sky-700" },
    email_soft_bounce: { label: "Email soft bounce", tone: "bg-amber-100 text-amber-700" },
    email_hard_bounce: { label: "Email hard bounce", tone: "bg-rose-100 text-rose-700" },
    email_blocked: { label: "Email blocked", tone: "bg-rose-100 text-rose-700" },
    email_spam: { label: "Email marked spam", tone: "bg-rose-100 text-rose-700" },
    email_unsubscribed: { label: "Recipient unsubscribed", tone: "bg-rose-100 text-rose-700" },
    email_complaint: { label: "Email complaint", tone: "bg-rose-100 text-rose-700" },
    email_deferred: { label: "Email deferred", tone: "bg-amber-100 text-amber-700" },
    email_invalid_email: { label: "Invalid recipient", tone: "bg-rose-100 text-rose-700" },
    email_error: { label: "Email error", tone: "bg-rose-100 text-rose-700" },
    email_loaded_by_proxy: { label: "Email opened (proxy)", tone: "bg-zinc-100 text-zinc-700" },
  };
  const info = labels[event.eventType] ?? {
    label: event.eventType,
    tone: "bg-zinc-100 text-zinc-700",
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", info.tone)}>
          {info.label}
        </span>
        <span className="text-xs text-muted-foreground">{formatted}</span>
      </div>
      {event.ipAddress && (
        <code className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {event.ipAddress}
        </code>
      )}
    </div>
  );
}

type ClientProfileOpportunity = {
  status: string;
  client?: {
    id: string;
    companyName: string;
    enrichmentStatus: "pending" | "running" | "succeeded" | "failed";
    industryType?: IndustryType | null;
  } | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactReferenceName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  estimatedPlan: string | null;
  estimatedSeats: number | null;
  estimatedMrrEur: number | null;
  computedMrrEur?: number | null;
  computedFixedMrrEur?: number | null;
  computedUsageMrrEur?: number | null;
};

function joinContactName(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string | null {
  const joined = [firstName, lastName].filter(Boolean).join(" ").trim();
  return joined.length > 0 ? joined : null;
}

function toNextActionInputValue(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// UTC (not local time) so the date shown/edited here always matches the
// calendar day the public landing page derives it as (public-proposal.tsx
// and quote-document.ts both use getUTC* for this).
function toUtcDateInputValue(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Mirrors the legacy implicit expiration rule (createdAt + 30 days) used
// whenever a proposal has no explicit expiresAt set.
function defaultExpiresAtInputValue(createdAt: Date | string | null | undefined): string {
  if (!createdAt) return "";
  const base = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
  if (Number.isNaN(base.getTime())) return "";
  const expiry = new Date(base);
  expiry.setUTCDate(expiry.getUTCDate() + 30);
  return toUtcDateInputValue(expiry);
}

function NextActionInline({
  opportunityId,
  nextActionAt,
}: {
  opportunityId: string;
  nextActionAt: Date | string | null;
}) {
  const utils = trpc.useUtils();
  const allQuery = trpc.opportunityTags.list.useQuery();
  const assignedQuery = trpc.opportunityTags.listForOpportunity.useQuery({ opportunityId });
  const update = trpc.opportunities.update.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate({ id: opportunityId });
    },
    onError: (e) => toast.error(e.message ?? "Failed to update next action"),
  });
  const setTags = trpc.opportunityTags.setForOpportunity.useMutation({
    onSuccess: () => {
      utils.opportunityTags.listForOpportunity.invalidate({ opportunityId });
    },
    onError: () => toast.error("Failed to save tag"),
  });

  const [open, setOpen] = useState(false);
  const [dateInput, setDateInput] = useState(toNextActionInputValue(nextActionAt));
  const [search, setSearch] = useState("");
  // Pending tag selection, committed together with the date on Save.
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const all = allQuery.data ?? [];
  const assigned = assignedQuery.data ?? [];

  // Seed the pending selection from the saved tags once per popover opening.
  useEffect(() => {
    if (open && !initializedRef.current && assignedQuery.data) {
      setSelectedTagIds(new Set(assignedQuery.data.map((t) => t.id)));
      initializedRef.current = true;
    }
    if (!open) initializedRef.current = false;
  }, [open, assignedQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((t) => t.name.toLowerCase().includes(q));
  }, [all, search]);

  const selectedTags = all.filter((t) => selectedTagIds.has(t.id));
  const nextAction = formatNextAction(nextActionAt);
  const isPending = update.isPending || setTags.isPending;

  const toggleTag = (tagId: string, checked: boolean) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(tagId);
      else next.delete(tagId);
      return next;
    });
  };

  const save = () => {
    const value = dateInput.trim();
    const next = value ? new Date(`${value}T12:00:00`) : null;
    if (value && (!next || Number.isNaN(next.getTime()))) {
      toast.error("Invalid date");
      return;
    }
    const originalIds = new Set(assigned.map((t) => t.id));
    const tagsChanged =
      originalIds.size !== selectedTagIds.size ||
      [...selectedTagIds].some((id) => !originalIds.has(id));
    if (tagsChanged) {
      setTags.mutate({ opportunityId, tagIds: [...selectedTagIds] });
    }
    update.mutate(
      { id: opportunityId, patch: { nextActionAt: next ? next.toISOString() : null } },
      { onSuccess: () => setOpen(false) }
    );
  };

  const clearDate = () => setDateInput("");

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setDateInput(toNextActionInputValue(nextActionAt));
          setSearch("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors hover:opacity-90",
            nextAction?.tone === "overdue" && "bg-rose-100 text-rose-700",
            nextAction?.tone === "today" && "bg-amber-100 text-amber-800",
            nextAction?.tone === "upcoming" && "bg-sky-100 text-sky-700",
            !nextAction &&
              "border border-dashed border-muted-foreground/40 bg-transparent text-muted-foreground hover:text-foreground"
          )}
          title="Set next action date and tags"
        >
          <CalendarClock className="h-3.5 w-3.5" />
          {nextAction ? `Next: ${nextAction.label}` : "Set next action"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="start">
        <div className="space-y-1">
          <label
            htmlFor="next-action-input"
            className="text-xs font-medium text-muted-foreground"
          >
            Next action date
          </label>
          <Calendar
            mode="single"
            selected={dateInput ? new Date(`${dateInput}T12:00:00`) : undefined}
            // Weekends aren't valid next-action days — disable them in the picker.
            disabled={(date) => isWeekend(date)}
            onSelect={(date) => setDateInput(date ? toNextActionInputValue(date) : "")}
            className="p-0"
          />
        </div>

        <div className="space-y-1.5 border-t pt-3">
          <span className="text-xs font-medium text-muted-foreground">Tags</span>
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedTags.map((t) => (
                <Badge
                  key={t.id}
                  variant="secondary"
                  className="gap-1 pl-2 pr-1 text-xs font-normal"
                  style={
                    t.color
                      ? { backgroundColor: `${t.color}1f`, borderColor: t.color, color: t.color }
                      : undefined
                  }
                >
                  {t.name}
                  <button
                    type="button"
                    onClick={() => toggleTag(t.id, false)}
                    className="rounded-full p-0.5 text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground"
                    aria-label={`Remove ${t.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags…"
              className="h-7 pl-7 text-xs"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {all.length === 0 ? (
              <div className="px-1 py-2 text-xs text-muted-foreground">
                No tags. Create them in Settings → Opportunity tags.
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-1 py-2 text-xs text-muted-foreground">Nothing found.</div>
            ) : (
              filtered.map((t) => (
                <label
                  key={t.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 text-xs hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={selectedTagIds.has(t.id)}
                    onChange={(e) => toggleTag(t.id, e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
                    style={{ backgroundColor: t.color ?? "transparent" }}
                    aria-hidden
                  />
                  <span className="flex-1">{t.name}</span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearDate}
            disabled={isPending || !dateInput}
          >
            Clear date
          </Button>
          <Button type="button" size="sm" onClick={save} disabled={isPending}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const NO_OWNER = "none";

/** Owner picker in the opportunity header — any signed-in user can be the owner. */
function OpportunityOwnerSelect({
  ownerId,
  onChange,
  disabled,
}: {
  ownerId: string | null;
  onChange: (ownerId: string | null) => void;
  disabled?: boolean;
}) {
  const usersQuery = trpc.users.listAssignable.useQuery();
  const users = usersQuery.data ?? [];
  const owner = users.find((u) => u.id === ownerId) ?? null;

  return (
    <Select
      value={ownerId ?? NO_OWNER}
      onValueChange={(v) => onChange(v === NO_OWNER ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger
        className="h-7 w-auto gap-1.5 rounded-full border bg-muted/40 px-2 py-1 text-xs font-medium text-muted-foreground shadow-none hover:bg-muted hover:text-foreground focus:ring-0"
        title="Opportunity owner"
      >
        <UserAvatar
          id={owner?.id ?? null}
          name={owner?.name}
          email={owner?.email}
          size="xs"
        />
        <span className="max-w-[10rem] truncate">
          {owner ? userLabel(owner.name, owner.email) : "Unassigned"}
        </span>
      </SelectTrigger>
      <SelectContent>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            <span className="flex items-center gap-2">
              <UserAvatar id={u.id} name={u.name} email={u.email} size="xs" />
              {userLabel(u.name, u.email)}
            </span>
          </SelectItem>
        ))}
        <SelectItem value={NO_OWNER}>Unassigned</SelectItem>
      </SelectContent>
    </Select>
  );
}

function OpportunityTagsInline({ opportunityId }: { opportunityId: string }) {
  const utils = trpc.useUtils();
  const allQuery = trpc.opportunityTags.list.useQuery();
  const assignedQuery = trpc.opportunityTags.listForOpportunity.useQuery({ opportunityId });
  const setMutation = trpc.opportunityTags.setForOpportunity.useMutation({
    onSuccess: () => {
      utils.opportunityTags.listForOpportunity.invalidate({ opportunityId });
    },
    onError: () => toast.error("Failed to save"),
  });

  const [search, setSearch] = useState("");

  const all = allQuery.data ?? [];
  const assigned = assignedQuery.data ?? [];
  const assignedIds = new Set(assigned.map((t) => t.id));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((t) => t.name.toLowerCase().includes(q));
  }, [all, search]);

  const toggle = (id: string, checked: boolean) => {
    const next = checked
      ? [...assignedIds, id]
      : [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ opportunityId, tagIds: next });
  };

  const remove = (id: string) => {
    const next = [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ opportunityId, tagIds: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {assignedQuery.isLoading ? (
        <Skeleton className="h-5 w-32" />
      ) : (
        assigned.map((t) => (
          <Badge
            key={t.id}
            variant="secondary"
            className="gap-1 pl-2 pr-1 text-xs font-normal"
            style={
              t.color
                ? { backgroundColor: `${t.color}1f`, borderColor: t.color, color: t.color }
                : undefined
            }
          >
            {t.name}
            <button
              type="button"
              onClick={() => remove(t.id)}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground"
              aria-label={`Remove ${t.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))
      )}
      <DropdownMenu onOpenChange={(open) => { if (!open) setSearch(""); }}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 gap-1 rounded-full border border-dashed border-muted-foreground/40 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3 w-3" />
            {assigned.length === 0 ? "Add tag" : "Add"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 p-0">
          <DropdownMenuLabel>Tags</DropdownMenuLabel>
          <div className="px-2 pb-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search…"
                className="h-7 pl-7 text-xs"
              />
            </div>
          </div>
          <DropdownMenuSeparator />
          <div className="max-h-64 overflow-y-auto">
            {all.length === 0 ? (
              <div className="px-2 py-3 text-xs text-muted-foreground">
                No tags. Create them in Settings → Opportunity tags.
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-xs text-muted-foreground">Nothing found.</div>
            ) : (
              filtered.map((t) => (
                <DropdownMenuCheckboxItem
                  key={t.id}
                  checked={assignedIds.has(t.id)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => toggle(t.id, !!checked)}
                >
                  <span
                    className="mr-2 inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
                    style={{ backgroundColor: t.color ?? "transparent" }}
                    aria-hidden
                  />
                  <span className="flex-1">{t.name}</span>
                </DropdownMenuCheckboxItem>
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ClientAccountingSystemsCard({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const allQuery = trpc.accountingSystems.list.useQuery();
  const assignedQuery = trpc.accountingSystems.listForClient.useQuery({ clientId });
  const setMutation = trpc.accountingSystems.setForClient.useMutation({
    onSuccess: () => {
      utils.accountingSystems.listForClient.invalidate({ clientId });
    },
    onError: () => toast.error("Failed to save"),
  });

  const [search, setSearch] = useState("");

  const all = allQuery.data ?? [];
  const assigned = assignedQuery.data ?? [];
  const assignedIds = new Set(assigned.map((s) => s.id));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.country ?? "").toLowerCase().includes(q)
    );
  }, [all, search]);

  const toggle = (id: string, checked: boolean) => {
    const next = checked
      ? [...assignedIds, id]
      : [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ clientId, accountingSystemIds: next });
  };

  const remove = (id: string) => {
    const next = [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ clientId, accountingSystemIds: next });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <Calculator className="h-3.5 w-3.5" />
          Accounting systems
        </CardTitle>
        <DropdownMenu onOpenChange={(open) => { if (!open) setSearch(""); }}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
              <Plus className="h-3 w-3" />
              Assign
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 p-0">
            <DropdownMenuLabel>Accounting systems</DropdownMenuLabel>
            <div className="px-2 pb-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search…"
                  className="h-7 pl-7 text-xs"
                />
              </div>
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-64 overflow-y-auto">
              {all.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  No systems. Create them on the "Accounting systems" page.
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  Nothing found.
                </div>
              ) : (
                filtered.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s.id}
                    checked={assignedIds.has(s.id)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => toggle(s.id, !!checked)}
                  >
                    <span className="flex-1">{s.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{s.country}</span>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        {assignedQuery.isLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : assigned.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No accounting systems assigned to this client.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {assigned.map((s) => (
              <Badge
                key={s.id}
                variant="secondary"
                className="gap-1 pl-2 pr-1 text-xs font-normal"
              >
                {s.name}
                <button
                  type="button"
                  onClick={() => remove(s.id)}
                  className="rounded-full p-0.5 text-secondary-foreground/80 hover:bg-white/20 hover:text-secondary-foreground"
                  aria-label={`Remove ${s.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClientCompanyDetails({ clientId }: { clientId: string }) {
  const clientQuery = trpc.clients.get.useQuery({ id: clientId });
  const c = clientQuery.data;

  if (clientQuery.isLoading) {
    return (
      <Card>
        <CardContent className="py-4">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!c) return null;

  const profile = c.profile ?? null;
  const hasContactInfo =
    c.website || c.linkedinUrl || c.email || c.phone || c.addressStreet || c.city || c.regCode || c.vatCode;
  const hasKeyInfo =
    c.industry ||
    c.accountingSystem ||
    c.headcount != null ||
    c.revenueEur != null ||
    c.foundedYear != null ||
    profile?.geographicReach ||
    profile?.pricingModel;
  const techItems = [
    ...(profile?.taxSoftware ?? []),
    ...(profile?.practiceManagement ?? []),
    ...(profile?.documentManagement ?? []),
    ...(profile?.technologies ?? []),
    ...(c.technologies ?? []),
  ];

  return (
    <>
      {profile?.summary && (
        <Card>
          <CardContent className="py-4 text-sm leading-relaxed">{profile.summary}</CardContent>
        </Card>
      )}

      {hasKeyInfo && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              Key information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <ClientField label="Industry" value={c.industry} />
            <ClientField label="Accounting system" value={c.accountingSystem} />
            <ClientField
              label="Headcount"
              value={
                c.headcount != null
                  ? `${c.headcount}${c.headcountSource ? ` (${c.headcountSource})` : ""}`
                  : null
              }
            />
            <ClientField
              label="Revenue"
              value={
                c.revenueEur != null
                  ? `€${c.revenueEur.toLocaleString("en-US")}${c.revenueYear ? ` (${c.revenueYear})` : ""}`
                  : null
              }
            />
            <ClientField label="Founded" value={c.foundedYear != null ? String(c.foundedYear) : null} />
            <ClientField label="Geography" value={profile?.geographicReach} />
            <ClientField label="Pricing" value={profile?.pricingModel} />
          </CardContent>
        </Card>
      )}

      {hasContactInfo && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {c.website && (
              <a
                href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-blue-600 hover:underline"
              >
                <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{c.website}</span>
              </a>
            )}
            {c.linkedinUrl && (
              <a
                href={c.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-blue-600 hover:underline"
              >
                <Linkedin className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">LinkedIn</span>
              </a>
            )}
            {c.email && (
              <a
                href={`mailto:${c.email}`}
                className="flex items-center gap-2 hover:text-blue-600 hover:underline"
              >
                <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{c.email}</span>
              </a>
            )}
            {c.phone && (
              <a
                href={`tel:${c.phone}`}
                className="flex items-center gap-2 hover:text-blue-600 hover:underline"
              >
                <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{c.phone}</span>
              </a>
            )}
            {(c.addressStreet || c.city) && (
              <div className="flex items-start gap-2 sm:col-span-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  {c.addressStreet && <div>{c.addressStreet}</div>}
                  {(c.postalCode || c.city) && (
                    <div className="text-muted-foreground">
                      {[c.postalCode, c.city].filter(Boolean).join(" ")}
                    </div>
                  )}
                </div>
              </div>
            )}
            {c.regCode && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>Reg. #: {c.regCode}</span>
              </div>
            )}
            {c.vatCode && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Banknote className="h-4 w-4" />
                <span>VAT: {c.vatCode}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {profile?.services && profile.services.length > 0 && (
        <ClientListCard title="Services" items={profile.services} />
      )}

      {techItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Technology
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <ClientChipRow label="Tax software" items={profile?.taxSoftware} />
            <ClientChipRow label="Practice management" items={profile?.practiceManagement} />
            <ClientChipRow label="Document management" items={profile?.documentManagement} />
            <ClientChipRow
              label="Other tech"
              items={[...(profile?.technologies ?? []), ...(c.technologies ?? [])]}
            />
          </CardContent>
        </Card>
      )}

      {profile?.decisionMakers && profile.decisionMakers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Decision makers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.decisionMakers.map((dm, i) => (
              <div key={i} className="rounded-md border p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="font-medium">{dm.name}</div>
                    {dm.title && (
                      <div className="text-xs text-muted-foreground">{dm.title}</div>
                    )}
                  </div>
                  {dm.linkedinUrl && (
                    <a
                      href={dm.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    >
                      <Linkedin className="h-3 w-3" />
                      LinkedIn
                    </a>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {dm.email && (
                    <a
                      href={`mailto:${dm.email}`}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      <Mail className="h-3 w-3" />
                      {dm.email}
                    </a>
                  )}
                  {dm.phone && (
                    <a
                      href={`tel:${dm.phone}`}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      <Phone className="h-3 w-3" />
                      {dm.phone}
                    </a>
                  )}
                  {dm.source && <span className="opacity-70">source: {dm.source}</span>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {profile?.buyingSignals && profile.buyingSignals.length > 0 && (
        <ClientListCard title="Buying signals" items={profile.buyingSignals} highlight />
      )}
    </>
  );
}

function ClientField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-sm", value ? "" : "text-muted-foreground/50")}>{value || "—"}</div>
    </div>
  );
}

function ClientListCard({
  title,
  items,
  highlight,
}: {
  title: string;
  items: string[];
  highlight?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li
              key={i}
              className={cn("text-sm", highlight && "font-medium text-emerald-700")}
            >
              · {it}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ClientChipRow({ label, items }: { label: string; items: string[] | undefined }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <Badge key={i} variant="secondary" className="font-normal">
            {it}
          </Badge>
        ))}
      </div>
    </div>
  );
}

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function isDocxDoc(d: { mimeType?: string | null; fileName?: string | null }): boolean {
  return (
    d.mimeType === DOCX_MIME ||
    (d.fileName ?? "").toLowerCase().endsWith(".docx")
  );
}

function isPreviewable(d: { mimeType?: string | null; fileName?: string | null }): boolean {
  const name = (d.fileName ?? "").toLowerCase();
  return (
    d.mimeType === DOCX_MIME ||
    d.mimeType === "application/pdf" ||
    name.endsWith(".docx") ||
    name.endsWith(".pdf")
  );
}

function OpportunityDocuments({ opportunityId }: { opportunityId: string }) {
  const utils = trpc.useUtils();
  const docsQuery = trpc.documents.list.useQuery({ opportunityId });
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track drag enter/leave depth so the drop overlay doesn't flicker as the
  // cursor moves across child elements of the card.
  const dragDepth = useRef(0);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [generatingQuote, setGeneratingQuote] = useState(false);

  const create = trpc.documents.create.useMutation({
    onSuccess: () => {
      utils.documents.list.invalidate({ opportunityId });
    },
    // Errors are surfaced per-file in uploadFiles so the toast can name the
    // offending file; no mutation-level handler to avoid duplicate toasts.
  });

  const remove = trpc.documents.delete.useMutation({
    onSuccess: () => {
      utils.documents.list.invalidate({ opportunityId });
      toast.success("Removed");
    },
    onError: () => toast.error("Failed to remove"),
  });

  const generateContract = trpc.contractTemplates.generateForOpportunity.useMutation({
    onSuccess: (data) => {
      utils.documents.list.invalidate({ opportunityId });
      setContractModalOpen(false);
      toast.success(
        data.reused
          ? "Sutartis jau buvo sugeneruota — naudojama esama."
          : "Sutartis sugeneruota."
      );
      // Open the preview right away so the agreement can be reviewed before
      // downloading or sending for signing. Prefer the .docx rendition so the
      // preview mirrors the source Word document (no LibreOffice pagination
      // quirks); fall back to the PDF if the .docx id is missing.
      const toPreview = data.documentId ?? data.pdfDocumentId;
      if (toPreview) setPreviewId(toPreview);
    },
    onError: (e) => toast.error(e.message ?? "Sutarties sugeneruoti nepavyko"),
  });

  // Render .docx documents (templates, generated agreements) natively from the
  // source Word file so the preview never inherits LibreOffice's pagination
  // quirks; fall back to the PDF renderer for actual PDFs. When the document
  // isn't in the list yet (e.g. just-generated agreement, list still
  // refetching) default to the .docx path since that's the only programmatic
  // open path.
  const previewDoc = (docsQuery.data ?? []).find((d) => d.id === previewId);
  const previewIsDocx = previewDoc ? isDocxDoc(previewDoc) : true;

  const htmlPreview = trpc.documents.previewHtml.useQuery(
    { id: previewId ?? "" },
    { enabled: !!previewId && previewIsDocx, staleTime: 60_000 }
  );
  const pdfPreview = trpc.documents.previewPdf.useQuery(
    { id: previewId ?? "" },
    { enabled: !!previewId && !previewIsDocx, staleTime: 60_000 }
  );
  const preview = previewIsDocx ? htmlPreview : pdfPreview;

  const handleDownload = async (id: string) => {
    try {
      const data = await utils.documents.download.fetch({ id });
      downloadBase64File(data.contentBase64, data.fileName, data.mimeType);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Atsisiųsti nepavyko");
    }
  };

  // Generates the same financial-summary quote PDF the customer can download
  // from the public landing page (the "Download PDF" button). Pulls the
  // assembled proposal payload — merged calculatorState, product catalog and
  // client variables — from the admin quoteData endpoint so the document is
  // byte-for-byte the one the client sees, then triggers a direct download.
  const handleGenerateQuote = async () => {
    setGeneratingQuote(true);
    try {
      const data = await utils.proposals.quoteData.fetch({ opportunityId });
      if (!data?.calculatorState) {
        toast.error(
          "Sukurkite pasiūlymą su produktais prieš generuojant kainų pasiūlymą."
        );
        setGeneratingQuote(false);
        return;
      }
      const meta = buildQuoteMetaFromVariables(
        data.variables as Record<string, unknown> | null,
        data.createdAt,
        data.expiresAt as string | Date | null
      );
      const { base64, fileName } = await generateQuotePdfBase64({
        state: data.calculatorState as CalculatorState,
        catalog: (data.productCatalog ?? []) as ProductCatalogItem[],
        language: (data.language ?? "lt") as "lt" | "en",
        meta,
      });
      await create.mutateAsync({
        name: fileName,
        opportunityId,
        fileName,
        mimeType: "application/pdf",
        fileBase64: base64,
      });
      toast.success("Kainų pasiūlymas įkeltas į dokumentus");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Kainų pasiūlymo sugeneruoti nepavyko"
      );
    } finally {
      setGeneratingQuote(false);
    }
  };

  const docs = docsQuery.data ?? [];
  const hasGeneratedContract = docs.some((d) => d.documentType === "contract");

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB, matches server limit

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of list) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      try {
        const fileBase64 = await readFileAsBase64(file);
        await create.mutateAsync({
          name: file.name,
          opportunityId,
          fileName: file.name,
          mimeType: file.type || null,
          fileBase64,
        });
        uploaded += 1;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      toast.success(
        uploaded === 1 ? "Document uploaded" : `${uploaded} documents uploaded`
      );
      setAdding(false);
    }
  };

  // The whole Documents card is a drop target — files can be dropped onto it at
  // any time without first clicking Add. Only react to actual file drags.
  const isFileDrag = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes("Files");

  const handleCardDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const handleCardDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault(); // required to allow a drop
  };
  const handleCardDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  };
  const handleCardDrop = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (!uploading && e.dataTransfer.files?.length) {
      void uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <Card
      className="relative"
      onDragEnter={handleCardDragEnter}
      onDragOver={handleCardDragOver}
      onDragLeave={handleCardDragLeave}
      onDrop={handleCardDrop}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm">
          <Upload className="h-7 w-7 text-primary" />
          <p className="text-sm font-medium text-primary">
            {uploading ? "Uploading…" : "Drop files to upload"}
          </p>
        </div>
      )}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />
          Documents
        </CardTitle>
        {!adding && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={handleGenerateQuote}
              disabled={generatingQuote}
              title="Sugeneruoti kainų pasiūlymo PDF (toks pat kaip kliento puslapyje)"
            >
              {generatingQuote ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Upload className="h-3 w-3" />
              )}
              {generatingQuote ? "Generuojama…" : "Generate quote"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={() => setContractModalOpen(true)}
              disabled={generateContract.isPending}
              title={
                hasGeneratedContract
                  ? "Sugeneruoti naują sutartį"
                  : "Sugeneruoti sutartį iš numatyto šablono"
              }
            >
              <FileSignature className="h-3 w-3" />
              {generateContract.isPending
                ? "Generuojama…"
                : hasGeneratedContract
                  ? "Pergeneruoti sutartį"
                  : "Sugeneruoti sutartį"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={() => setAdding(true)}
            >
              <Plus className="h-3 w-3" />
              Add
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <div className="space-y-2">
            <div
              role="button"
              tabIndex={0}
              onClick={() => !uploading && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !uploading) {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              className={cn(
                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/25 bg-muted/30 p-6 text-center transition-colors hover:bg-muted/50",
                uploading && "pointer-events-none opacity-60"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) void uploadFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-6 w-6 text-muted-foreground" />
              )}
              <p className="text-sm font-medium">
                {uploading ? "Uploading…" : "Drag & drop files here"}
              </p>
              <p className="text-xs text-muted-foreground">
                or click to browse · max 10MB
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={uploading}
                onClick={() => setAdding(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {docsQuery.isLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents.</p>
        ) : (
          <ul className="space-y-2">
            {docs.map((d) => (
              <li
                key={d.id}
                className="flex items-start justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate">{d.name}</span>
                  </div>
                  {d.documentUrl ? (
                    <a
                      href={d.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="truncate">{d.documentUrl}</span>
                    </a>
                  ) : null}
                  {d.fileName ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      <span className="font-mono">{d.fileName}</span>
                      {(d.documentType === "contract" ||
                        d.documentType === "contract_pdf") && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">
                          Sutartis
                        </Badge>
                      )}
                    </p>
                  ) : null}
                  {d.description ? (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                      {d.description}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  {d.fileSize && d.fileSize > 0 && isPreviewable(d) ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => setPreviewId(d.id)}
                      aria-label="Preview"
                      title="Peržiūrėti"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {d.fileSize && d.fileSize > 0 ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleDownload(d.id)}
                      aria-label="Download"
                      title="Atsisiųsti"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => remove.mutate({ id: d.id })}
                    disabled={remove.isPending}
                    aria-label="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <GenerateContractModal
        open={contractModalOpen}
        onOpenChange={setContractModalOpen}
        opportunityId={opportunityId}
        pending={generateContract.isPending}
        onGenerate={(args) =>
          generateContract.mutate({
            opportunityId,
            force: true,
            contractTemplateId: args.templateId,
            overrides: args.overrides,
            customVariables: args.customVariables,
          })
        }
      />

      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{preview.data?.name ?? "Dokumento peržiūra"}</DialogTitle>
            <DialogDescription>
              {preview.data?.fileName
                ? `${preview.data.fileName} · ${previewIsDocx ? "Word (.docx) peržiūra." : "PDF peržiūra."}`
                : previewIsDocx
                  ? "Word (.docx) peržiūra."
                  : "PDF peržiūra."}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[75vh] overflow-hidden rounded-md border bg-muted">
            {preview.isLoading && (
              <div className="space-y-2 p-6">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-full" />
              </div>
            )}
            {preview.isError && (
              <p className="p-6 text-sm text-destructive">{preview.error.message}</p>
            )}
            {previewIsDocx
              ? htmlPreview.data && (
                  <DocxPreview
                    html={htmlPreview.data.html}
                    className="h-full w-full overflow-auto"
                  />
                )
              : pdfPreview.data && (
                  <PdfPreview
                    base64={pdfPreview.data.pdfBase64}
                    title={pdfPreview.data.name}
                    className="h-full w-full"
                  />
                )}
          </div>
          <DialogFooter>
            {previewId && (
              <Button variant="outline" onClick={() => handleDownload(previewId)}>
                <Download className="h-4 w-4" />
                Atsisiųsti
              </Button>
            )}
            <Button onClick={() => setPreviewId(null)}>Uždaryti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const CONTRACT_GROUP_LABELS: Record<string, string> = {
  company: "Jūsų įmonė",
  client: "Klientas",
  opportunity: "Pasiūlymas",
  pricing: "Kainos",
  date: "Data",
};
const CONTRACT_GROUP_ORDER = ["company", "client", "opportunity", "pricing", "date"];

type CustomVariableRow = { id: number; name: string; value: string };

function GenerateContractModal({
  open,
  onOpenChange,
  opportunityId,
  pending,
  onGenerate,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  opportunityId: string;
  pending: boolean;
  onGenerate: (args: {
    templateId: string;
    overrides: Record<string, string>;
    customVariables: { name: string; value: string }[];
  }) => void;
}) {
  const prepareQuery = trpc.contractTemplates.prepareForOpportunity.useQuery(
    { opportunityId },
    { enabled: open, refetchOnWindowFocus: false }
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [customVars, setCustomVars] = useState<CustomVariableRow[]>([]);
  const nextCustomId = useRef(1);

  // Reset the form to the freshly fetched known values whenever the modal opens
  // or the prepared data changes.
  const data = prepareQuery.data;
  useEffect(() => {
    if (!open || !data) return;
    const initial: Record<string, string> = {};
    for (const field of data.fields) initial[field.key] = field.value;
    setValues(initial);
    setCustomVars([]);
    nextCustomId.current = 1;
  }, [open, data]);

  type PreparedField = NonNullable<typeof data>["fields"][number];
  const groupedFields = useMemo(() => {
    const groups = new Map<string, PreparedField[]>();
    if (!data) return [] as Array<{ group: string; fields: PreparedField[] }>;
    for (const field of data.fields) {
      const arr = groups.get(field.group) ?? [];
      arr.push(field);
      groups.set(field.group, arr);
    }
    return CONTRACT_GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({
      group: g,
      fields: groups.get(g)!,
    }));
  }, [data]);

  const missingCount = data?.fields.filter((f) => !values[f.key]?.trim()).length ?? 0;

  const handleSubmit = () => {
    if (!data) return;
    const overrides: Record<string, string> = {};
    for (const field of data.fields) overrides[field.key] = values[field.key] ?? "";
    const customVariables = customVars
      .map((c) => ({ name: c.name.trim(), value: c.value }))
      .filter((c) => c.name.length > 0);
    onGenerate({ templateId: data.templateId, overrides, customVariables });
  };

  const addCustomVar = () => {
    setCustomVars((prev) => [
      ...prev,
      { id: nextCustomId.current++, name: "", value: "" },
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Sugeneruoti sutartį</DialogTitle>
          <DialogDescription>
            Patikrinkite kintamųjų reikšmes. Tuščius laukus užpildykite — pakeitimai
            bus išsaugoti ir įrašyti į sutartį.
          </DialogDescription>
        </DialogHeader>

        {prepareQuery.isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : prepareQuery.isError ? (
          <p className="py-6 text-sm text-destructive">
            {prepareQuery.error.message}
          </p>
        ) : data ? (
          <div className="space-y-5 overflow-y-auto pr-1" style={{ maxHeight: "55vh" }}>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                Šablonas: <span className="font-medium text-foreground">{data.templateName}</span>
              </span>
              {missingCount > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  Trūksta {missingCount}
                </Badge>
              )}
            </div>

            {groupedFields.map(({ group, fields }) => (
              <div key={group} className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {CONTRACT_GROUP_LABELS[group] ?? group}
                </h4>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {fields.map((field) => {
                    const empty = !values[field.key]?.trim();
                    return (
                      <div key={field.key} className="space-y-1">
                        <Label className="flex items-center gap-1.5 text-xs">
                          {field.label}
                          {empty && (
                            <span className="text-[10px] font-normal text-amber-600">
                              trūksta
                            </span>
                          )}
                        </Label>
                        <Input
                          value={values[field.key] ?? ""}
                          onChange={(e) =>
                            setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          className={`h-8 text-sm ${empty ? "border-amber-400 focus-visible:ring-amber-400" : ""}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Papildomi kintamieji
                </h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  onClick={addCustomVar}
                >
                  <Plus className="h-3 w-3" />
                  Pridėti
                </Button>
              </div>
              {customVars.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Pridėkite savo kintamuosius, jei šablonas naudoja papildomus laukus.
                </p>
              ) : (
                <div className="space-y-2">
                  {customVars.map((row) => (
                    <div key={row.id} className="flex items-center gap-2">
                      <Input
                        value={row.name}
                        onChange={(e) =>
                          setCustomVars((prev) =>
                            prev.map((c) =>
                              c.id === row.id ? { ...c, name: e.target.value } : c
                            )
                          )
                        }
                        placeholder="kintamojo_vardas"
                        className="h-8 flex-1 font-mono text-xs"
                      />
                      <Input
                        value={row.value}
                        onChange={(e) =>
                          setCustomVars((prev) =>
                            prev.map((c) =>
                              c.id === row.id ? { ...c, value: e.target.value } : c
                            )
                          )
                        }
                        placeholder="reikšmė"
                        className="h-8 flex-1 text-sm"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          setCustomVars((prev) => prev.filter((c) => c.id !== row.id))
                        }
                        aria-label="Pašalinti"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Atšaukti
          </Button>
          <Button onClick={handleSubmit} disabled={pending || !data} className="gap-1">
            {pending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Generuojama…
              </>
            ) : (
              <>
                <FileSignature className="h-3.5 w-3.5" />
                Sugeneruoti
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type ContactPickerItem = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  jobTitle: string | null;
  clientId: string | null;
  clientName?: string | null;
};

function SelectContactDialog({
  open,
  onOpenChange,
  clientId,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId: string | null;
  onSelect: (c: ContactPickerItem) => void;
}) {
  const [search, setSearch] = useState("");
  const list = trpc.contacts.list.useQuery(
    { search: search || undefined },
    { enabled: open }
  );
  const all = (list.data ?? []) as ContactPickerItem[];

  const rows = useMemo(() => {
    if (!clientId) return all;
    const own = all.filter((c) => c.clientId === clientId);
    const others = all.filter((c) => c.clientId !== clientId);
    return [...own, ...others];
  }, [all, clientId]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Pick a contact</DialogTitle>
          <DialogDescription>
            Choose an existing contact — name, email, and phone will be
            attached to this opportunity.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by name, email, company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="max-h-80 space-y-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No contacts found.
            </div>
          ) : (
            rows.map((c) => {
              const name =
                [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                c.email ||
                "—";
              const isOwnClient = clientId != null && c.clientId === clientId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelect(c)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 rounded-md border bg-background/60 px-3 py-2 text-left text-sm hover:bg-muted"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      <span className="truncate">{name}</span>
                      {isOwnClient && (
                        <Badge variant="secondary" className="text-[10px] font-normal">
                          this client
                        </Badge>
                      )}
                    </div>
                    {(c.jobTitle || c.clientName) && (
                      <div className="truncate text-xs text-muted-foreground">
                        {[c.jobTitle, c.clientName].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                    {c.email && <span className="truncate">{c.email}</span>}
                    {(c.phone || c.mobilePhone) && (
                      <span className="truncate">{c.phone ?? c.mobilePhone}</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientTypeSelect({
  value,
  onChange,
  disabled,
}: {
  value: IndustryType | null;
  onChange: (v: IndustryType | null) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      value={value ?? "__unset__"}
      disabled={disabled}
      onValueChange={(v) =>
        onChange(v === "__unset__" ? null : (v as IndustryType))
      }
    >
      <SelectTrigger
        className="h-8 w-auto gap-1 border-0 bg-transparent p-0 shadow-none focus:ring-0"
        title="Client type"
      >
        <span
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-semibold",
            value
              ? INDUSTRY_TYPE_LABELS[value].color
              : "border-dashed border-muted-foreground/30 text-muted-foreground"
          )}
        >
          {value ? INDUSTRY_TYPE_LABELS[value].label : "Client type"}
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__unset__">
          <span className="text-xs text-muted-foreground">Not set</span>
        </SelectItem>
        {(Object.keys(INDUSTRY_TYPE_LABELS) as IndustryType[]).map((t) => (
          <SelectItem key={t} value={t}>
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                INDUSTRY_TYPE_LABELS[t].color
              )}
            >
              {INDUSTRY_TYPE_LABELS[t].label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ClientProfileCard({
  o,
  brief,
  statusInfo,
  editingField,
  fieldValues,
  startEdit,
  saveField,
  onCancel,
  onChange,
}: {
  o: ClientProfileOpportunity;
  brief: Brief | null;
  statusInfo: { label: string; color: string };
  editingField: string | null;
  fieldValues: Record<string, string>;
  startEdit: (f: string, v: string | null) => void;
  saveField: (f: string) => void;
  onCancel: () => void;
  onChange: (f: string, v: string) => void;
}) {
  const utils = trpc.useUtils();
  const updateClient = trpc.clients.update.useMutation({
    onSuccess: () => {
      utils.opportunities.get.invalidate();
      utils.opportunities.list.invalidate();
    },
    onError: () => toast.error("Failed to update client type"),
  });
  const company = o.client?.companyName ?? brief?.client?.company ?? null;
  const contactName =
    joinContactName(o.contactFirstName, o.contactLastName) ??
    brief?.client?.name ??
    null;
  const industry = brief?.client?.industry;
  const size = brief?.client?.size;
  const initials = getInitials(company ?? contactName ?? "?");
  const enrichmentLabel =
    o.client?.enrichmentStatus === "running"
      ? "preparing…"
      : o.client?.enrichmentStatus === "failed"
        ? "preparation failed"
        : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-sm font-semibold text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold leading-tight">
                {company ?? <span className="text-muted-foreground">No company</span>}
              </h2>
              {o.client && (
                <ClientTypeSelect
                  value={o.client.industryType ?? null}
                  onChange={(v) =>
                    updateClient.mutate({
                      id: o.client!.id,
                      patch: { industryType: v },
                    })
                  }
                  disabled={updateClient.isPending}
                />
              )}
              <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", statusInfo.color)}>
                {statusInfo.label}
              </span>
            </div>
            {(industry || size) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {industry && (
                  <Badge variant="secondary" className="gap-1 font-normal">
                    <Briefcase className="h-3 w-3" />
                    {industry}
                  </Badge>
                )}
                {size && (
                  <Badge variant="secondary" className="gap-1 font-normal">
                    <Users className="h-3 w-3" />
                    {size}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
      </CardContent>

    </Card>
  );
}

function Metric({
  icon,
  label,
  value,
  suffix,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
  tone?: "dark";
}) {
  const labelClass =
    tone === "dark"
      ? "text-[10px] font-semibold uppercase tracking-wide text-white/60"
      : "text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";
  const suffixClass =
    tone === "dark"
      ? "ml-0.5 text-xs font-normal text-white/60"
      : "ml-0.5 text-xs font-normal text-muted-foreground";
  return (
    <div className="flex flex-col gap-0.5">
      <div className={cn("flex items-center gap-1", labelClass)}>
        {icon}
        {label}
      </div>
      <div className="truncate text-sm font-semibold">
        {value}
        {suffix && <span className={suffixClass}>{suffix}</span>}
      </div>
    </div>
  );
}

function PublicUrlBadge({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/p/${slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div
      title={url}
      className="group flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Link2 className="h-3 w-3" />
      <span className="max-w-[220px] truncate font-mono">/p/{slug}</span>
      <button
        type="button"
        onClick={copy}
        title="Copy link"
        className="flex items-center transition-opacity hover:text-foreground"
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-600" />
        ) : (
          <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />
        )}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in new tab"
        className="flex items-center transition-opacity hover:text-foreground"
      >
        <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" />
      </a>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function NoteRow({
  note,
  onDelete,
  deleting,
  onSave,
  saving,
  onReply,
}: {
  note: {
    id: string;
    content: string;
    createdAt: Date | string;
    type?: "note" | "email" | "landing_response" | "landing_view" | null;
    label?: string | null;
    authorName?: string | null;
    authorEmail?: string | null;
  };
  onDelete: () => void;
  deleting: boolean;
  onSave: (content: string) => void;
  saving: boolean;
  onReply?: (toEmail: string, subject: string | null) => void;
}) {
  const createdAt = new Date(note.createdAt);
  const isEmail = note.type === "email";
  const isLanding = note.type === "landing_response";
  const isLandingView = note.type === "landing_view";
  // Inbound client email replies are captured by the Gmail poller as notes
  // labelled "client_reply" (with the sender stored in authorEmail). They can
  // be replied to directly via the message dialog.
  const isClientReply = note.label === "client_reply";
  const isEditable = (!note.type || note.type === "note") && !isClientReply;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const isEmailOut = isEmail && note.label === "hubspot-email-out";
  const isEmailIn = isEmail && note.label === "hubspot-email-in";
  const isEmailFwd = isEmail && note.label === "hubspot-email-fwd";
  const isClient = isLanding && note.label === "client";
  // The sender email this reply should go to, and the original subject parsed
  // from the note body ("Subject: ..." line written by the Gmail poller).
  const replyToEmail = isClientReply ? (note.authorEmail ?? null) : null;
  const replySubject = isClientReply
    ? (note.content.match(/^Subject:\s*(.+)$/m)?.[1]?.trim() ?? null)
    : null;
  const canReply = Boolean(isClientReply && onReply && replyToEmail);
  const [expanded, setExpanded] = useState(false);
  const COLLAPSE_LINE_LIMIT = 6;
  const COLLAPSE_CHAR_LIMIT = 320;
  const lineCount = note.content.split("\n").length;
  const isLong =
    lineCount > COLLAPSE_LINE_LIMIT || note.content.length > COLLAPSE_CHAR_LIMIT;
  const authorParts = [note.authorName, note.authorEmail].filter(Boolean).join(" · ");
  const Icon = isEmail || isClientReply ? Mail : isLandingView ? Eye : StickyNote;
  const typeBadge: { label: string; tone: string } = isClientReply
    ? { label: "Email · Client reply", tone: "bg-amber-100 text-amber-800" }
    : isEmailIn
    ? { label: "Email · Received", tone: "bg-amber-100 text-amber-800" }
    : isEmailFwd
      ? { label: "Email · Forwarded", tone: "bg-slate-100 text-slate-700" }
      : isEmailOut
        ? { label: "Email · Sent", tone: "bg-indigo-100 text-indigo-800" }
        : isEmail
          ? { label: "Email", tone: "bg-indigo-100 text-indigo-800" }
          : isLandingView
            ? { label: "Landing · Viewed", tone: "bg-emerald-100 text-emerald-800" }
            : note.label === "client_question"
              ? { label: "Landing · Question", tone: "bg-blue-100 text-blue-700" }
              : note.label === "client_requirements"
                ? { label: "Landing · Requirements", tone: "bg-blue-100 text-blue-700" }
                : note.label === "client_extras"
                  ? { label: "Landing · Extras", tone: "bg-blue-100 text-blue-700" }
                  : isClient
                    ? { label: "Landing · Feedback", tone: "bg-blue-100 text-blue-700" }
                    : isLanding
                      ? { label: "Landing response", tone: "bg-blue-100 text-blue-700" }
                      : { label: "Note", tone: "bg-zinc-100 text-zinc-700" };
  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-md border p-3",
        isLanding && "border-blue-200 bg-blue-50/40",
        isLandingView && "border-emerald-200 bg-emerald-50/40",
        (isEmailIn || isClientReply) && "border-amber-200 bg-amber-50/40",
        isEmailOut && "border-indigo-200 bg-indigo-50/40",
        isEmailFwd && "border-slate-200 bg-slate-50/40"
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          isLanding
            ? "text-blue-600"
            : isLandingView
              ? "text-emerald-700"
              : isEmailIn || isClientReply
                ? "text-amber-700"
                : isEmailOut
                  ? "text-indigo-700"
                  : isEmailFwd
                    ? "text-slate-600"
                    : "text-muted-foreground"
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]",
              typeBadge.tone
            )}
          >
            {typeBadge.label}
          </span>
          {authorParts && (
            <span className="text-[11px] text-muted-foreground">{authorParts}</span>
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  const trimmed = draft.trim();
                  if (trimmed && trimmed !== note.content.trim()) onSave(trimmed);
                  setEditing(false);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setDraft(note.content);
                  setEditing(false);
                }
              }}
              autoFocus
              className="min-h-[80px] text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(note.content);
                  setEditing(false);
                }}
                disabled={saving}
                className="gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const trimmed = draft.trim();
                  if (trimmed && trimmed !== note.content.trim()) onSave(trimmed);
                  setEditing(false);
                }}
                disabled={saving || !draft.trim()}
                className="gap-1.5"
              >
                <Check className="h-3.5 w-3.5" />
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {isEditable ? (
              <div
                className={cn(
                  "relative",
                  isLong && !expanded && "max-h-40 overflow-hidden"
                )}
              >
                <Markdown content={note.content} />
                {isLong && !expanded && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent" />
                )}
              </div>
            ) : (
              <p
                className={cn(
                  "whitespace-pre-wrap text-sm leading-relaxed",
                  isLong && !expanded && "line-clamp-6"
                )}
              >
                {note.content}
              </p>
            )}
            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3 w-3" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" /> Show more
                  </>
                )}
              </button>
            )}
          </>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          {createdAt.toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
        {canReply && (
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              onClick={() => onReply!(replyToEmail!, replySubject)}
            >
              <Reply className="h-3.5 w-3.5" />
              Reply
            </Button>
          </div>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {isEditable && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setDraft(note.content);
                setEditing(true);
              }}
              aria-label="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={deleting}
            aria-label="Delete note"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function formatEur(cents: number) {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

type ProductGroupKey =
  | "seats"
  | "core"
  | "hub"
  | "onboarding"
  | "accounting_system"
  | "other";

const PRODUCT_GROUP_ORDER: Array<{ key: ProductGroupKey; label: string }> = [
  { key: "seats", label: "SEAT PLAN" },
  { key: "core", label: "STORAGE PLAN" },
  { key: "hub", label: "HUB SERVICES" },
  { key: "onboarding", label: "ONBOARDING" },
  { key: "accounting_system", label: "ACCOUNTING SYSTEM" },
  { key: "other", label: "OTHER" },
];

function groupProductsByCategory<T extends { stripe_product_id: string }>(
  items: T[],
  productsByStripeId: Map<string, { category: string | null; subCategory: string | null }>
): Array<{ key: ProductGroupKey; label: string; items: T[] }> {
  const buckets: Record<ProductGroupKey, T[]> = {
    seats: [],
    core: [],
    hub: [],
    onboarding: [],
    accounting_system: [],
    other: [],
  };
  for (const it of items) {
    const info = productsByStripeId.get(it.stripe_product_id);
    const cat = info?.category ?? null;
    const sub = info?.subCategory ?? null;
    if (cat === "accounting_system") buckets.accounting_system.push(it);
    else if (sub === "seats") buckets.seats.push(it);
    else if (sub === "core") buckets.core.push(it);
    else if (sub === "hub") buckets.hub.push(it);
    else if (sub === "onboarding") buckets.onboarding.push(it);
    else buckets.other.push(it);
  }
  return PRODUCT_GROUP_ORDER.filter((g) => buckets[g.key].length > 0).map((g) => ({
    key: g.key,
    label: g.label,
    items: buckets[g.key],
  }));
}

type ServicePrice = {
  stripePriceId: string;
  unitAmount: number | null;
  currency: string | null;
  recurringInterval: string | null;
  recurringIntervalCount: number | null;
  isDefault: boolean | null;
  priceType: string | null;
  billingScheme: string | null;
  usageType: string | null;
  tiersMode: string | null;
  tiers: Array<Record<string, unknown>> | null;
  industry: IndustryType | null;
};

// For tiered prices (billing_scheme=tiered), Stripe leaves unit_amount=null
// and the headline unit lives on the first paid tier. Fall back to that
// so the quote builder doesn't render €0 for tiered products.
function effectiveUnitAmount(price: ServicePrice | null | undefined): number | null {
  if (!price) return null;
  if (price.unitAmount != null) return price.unitAmount;
  const tiers = price.tiers ?? [];
  for (const raw of tiers) {
    const unit = readTierUnitCents(raw);
    if (unit != null && unit > 0) return unit;
    const flat = readNumber(raw.flat_amount);
    if (flat != null && flat > 0) return flat;
  }
  return null;
}

function readNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readTierUnitCents(raw: Record<string, unknown>): number | null {
  const unit = readNumber(raw.unit_amount);
  if (unit != null) return unit;
  return readNumber(raw.unit_amount_decimal);
}

type ServiceItem = {
  id: string;
  stripeProductId: string;
  name: string;
  category: string | null;
  subCategory: string | null;
  isDefault: boolean;
  defaultPriceCents: number | null;
  defaultPriceId: string | null;
  defaultQty: number | null;
  currency: string | null;
  recurringInterval: string | null;
  prices: ServicePrice[];
};

// Opportunities without an explicit industry type fall back to "accounting",
// matching the server default and the value every existing client was
// backfilled to.
const DEFAULT_INDUSTRY: IndustryType = "accounting";

// Strict industry pricing: a product that carries any industry-tagged price is
// segmented, so we only ever use prices for the opportunity's own industry —
// never the other industry's price, never an untagged one. Products with no
// industry tags at all are universal and keep using their untagged prices.
function pickPriceForPeriod(
  service: ServiceItem,
  period: BillingPeriod,
  industry: IndustryType | null | undefined
): ServicePrice | null {
  const targetInterval = period === "monthly" ? "month" : "year";
  const resolvedIndustry = industry ?? DEFAULT_INDUSTRY;
  const all = service.prices ?? [];
  const isSegmented = all.some((p) => p.industry != null);
  const pool = isSegmented
    ? all.filter((p) => p.industry === resolvedIndustry)
    : all;
  if (pool.length === 0) return null;
  const matching = pool.filter((p) => p.recurringInterval === targetInterval);
  if (matching.length > 0) {
    return matching.find((p) => p.isDefault) ?? matching[0];
  }
  if (service.defaultPriceId) {
    const def = pool.find((p) => p.stripePriceId === service.defaultPriceId);
    if (def) return def;
  }
  return pool.find((p) => p.isDefault) ?? pool[0] ?? null;
}

// A flat-fee line (e.g. the business seat plan) charges a fixed amount no
// matter the quantity, so the quote shows an amount of 1 (per account) and the
// amount control is locked.
function lineIsFlatFee(line: { tiers_mode?: string | null; tiers?: Array<Record<string, unknown>> | null }): boolean {
  return priceTiersAreFlatFee(line.tiers_mode, line.tiers);
}

function hasPeriod(
  service: ServiceItem,
  period: BillingPeriod,
  industry: IndustryType | null | undefined
): boolean {
  const target = period === "monthly" ? "month" : "year";
  const resolvedIndustry = industry ?? DEFAULT_INDUSTRY;
  const all = service.prices ?? [];
  const isSegmented = all.some((p) => p.industry != null);
  const pool = isSegmented
    ? all.filter((p) => p.industry === resolvedIndustry)
    : all;
  return pool.some((p) => p.recurringInterval === target);
}

function translateProductName(
  language: "lt" | "en",
  info: ProductInfo | undefined,
  fallback: string
): string {
  if (!info) return fallback;
  const primary = language === "lt" ? info.nameLt : info.nameEn;
  const secondary = language === "lt" ? info.nameEn : info.nameLt;
  return primary ?? secondary ?? info.name ?? fallback;
}

function buildSeedState(
  services: ServiceItem[],
  selectedProducts: SelectedProductCard[],
  period: BillingPeriod,
  industry: IndustryType | null | undefined,
  prevExtraSelected?: Map<string, boolean>,
  prevExtraHidden?: Map<string, boolean>
): QuoteState {
  const selectedById = new Map(selectedProducts.map((p) => [p.stripe_product_id, p]));
  const main: ProposalProduct[] = [];
  const extra: ProposalProduct[] = [];
  const seenExtras = new Set<string>();
  const carrySelected = (pid: string): boolean =>
    prevExtraSelected?.get(pid) ?? false;
  const carryHidden = (pid: string): boolean => prevExtraHidden?.get(pid) ?? false;
  for (const s of services) {
    const sel = selectedById.get(s.stripeProductId);
    if (!sel) continue;
    const price = pickPriceForPeriod(s, period, industry);
    const usageType = price?.usageType === "metered" ? "metered" : "licensed";
    const originalAmount = effectiveUnitAmount(price) ?? s.defaultPriceCents ?? 0;
    const discountCents = sel.discount_cents ?? 0;
    const hasDiscount = discountCents > 0 && discountCents !== originalAmount;
    const isFlatFee = priceTiersAreFlatFee(price?.tiersMode, price?.tiers);
    const line: ProposalProduct = {
      stripe_product_id: s.stripeProductId,
      stripe_price_id: price?.stripePriceId ?? s.defaultPriceId ?? "",
      qty: isFlatFee ? 1 : sel.qty ?? 0,
      usage: 0,
      unit_amount: hasDiscount ? discountCents : originalAmount,
      ...(hasDiscount
        ? { original_unit_amount: originalAmount, discount_cents: discountCents }
        : {}),
      usage_type: usageType,
      tiers_mode: price?.tiersMode ?? null,
      tiers: price?.tiers ?? null,
      name: s.name,
    };
    if (sel.kind === "main") {
      main.push(line);
    } else {
      extra.push({
        ...line,
        selected: carrySelected(s.stripeProductId),
        hidden: carryHidden(s.stripeProductId),
      });
      seenExtras.add(s.stripeProductId);
    }
  }
  for (const s of services) {
    if (s.subCategory !== "hub" && s.subCategory !== "onboarding") continue;
    if (seenExtras.has(s.stripeProductId)) continue;
    const price = pickPriceForPeriod(s, period, industry);
    const usageType = price?.usageType === "metered" ? "metered" : "licensed";
    const isFlatFee = priceTiersAreFlatFee(price?.tiersMode, price?.tiers);
    extra.push({
      stripe_product_id: s.stripeProductId,
      stripe_price_id: price?.stripePriceId ?? s.defaultPriceId ?? "",
      qty: isFlatFee ? 1 : s.defaultQty ?? 1,
      usage: 0,
      unit_amount: effectiveUnitAmount(price) ?? s.defaultPriceCents ?? 0,
      usage_type: usageType,
      tiers_mode: price?.tiersMode ?? null,
      tiers: price?.tiers ?? null,
      name: s.name,
      selected: carrySelected(s.stripeProductId),
      hidden: carryHidden(s.stripeProductId),
    });
  }
  return { billing_period: period, main, extra, currency: "EUR" };
}

function OpportunityPerksCatalog({
  opportunityId,
  language,
  proposals,
}: {
  opportunityId: string;
  language: "lt" | "en";
  proposals: ProposalRowForQuote[];
}) {
  const utils = trpc.useUtils();
  const catalogQuery = trpc.perkCatalog.list.useQuery();
  const catalog = catalogQuery.data ?? [];
  const targetProposal = useMemo(() => {
    if (proposals.length === 0) return null;
    const editable = [...proposals].reverse().find((p) => p.status !== "accepted");
    return editable ?? proposals[proposals.length - 1];
  }, [proposals]);

  const perks: Perk[] = (targetProposal?.perks ?? []) as Perk[];

  const updateProposal = trpc.proposals.update.useMutation({
    onSuccess: () => {
      utils.proposals.list.invalidate({ opportunityId });
    },
    onError: (e) => toast.error(e.message ?? "Failed to save perks"),
  });

  const togglePerk = (catalogId: string, enabled: boolean) => {
    if (!targetProposal) return;
    const entry = catalog.find((c) => c.id === catalogId);
    if (!entry) return;
    const title = language === "lt" ? entry.titleLt : entry.titleEn;
    const description =
      language === "lt" ? entry.descriptionLt : entry.descriptionEn;
    const existing = perks.find((p) => p.id === catalogId);
    let next: Perk[];
    if (enabled) {
      const merged: Perk = {
        id: entry.id,
        enabled: true,
        title,
        description,
        amount: entry.amount,
      };
      next = existing
        ? perks.map((p) => (p.id === catalogId ? merged : p))
        : [...perks, merged];
    } else {
      next = perks.filter((p) => p.id !== catalogId);
    }
    updateProposal.mutate({ id: targetProposal.id, patch: { perks: next } });
  };

  const removeOrphanPerk = (perkId: string) => {
    if (!targetProposal) return;
    const next = perks.filter((p) => p.id !== perkId);
    updateProposal.mutate({ id: targetProposal.id, patch: { perks: next } });
  };

  const orphanPerks = useMemo(() => {
    if (catalog.length === 0) return [] as Perk[];
    const catalogIds = new Set(catalog.map((c) => c.id));
    return perks.filter((p) => !catalogIds.has(p.id));
  }, [perks, catalog]);

  const isReadOnly = !!targetProposal && targetProposal.status === "accepted";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
          <Gift className="h-3.5 w-3.5" />
          Perks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {catalogQuery.isLoading ? (
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            Loading perks…
          </p>
        ) : catalog.length === 0 ? (
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            No perks configured. Add some in{" "}
            <Link to="/settings/perks" className="underline underline-offset-2">
              Settings → Perks
            </Link>
            .
          </p>
        ) : (
          <>
            {!targetProposal && (
              <p className="text-[12px] leading-relaxed text-muted-foreground">
                Create a proposal first to enable perks.
              </p>
            )}
            {orphanPerks.map((perk) => (
              <div
                key={perk.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">{perk.title}</div>
                    {perk.amount && (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground tabular-nums">
                        {perk.amount}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    {perk.description}
                  </p>
                  <p className="mt-1 text-[10.5px] font-medium uppercase tracking-wide text-amber-700">
                    Not in catalog — won't translate. Remove or recreate it in Settings → Perks.
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={updateProposal.isPending || isReadOnly}
                  onClick={() => removeOrphanPerk(perk.id)}
                  aria-label={`Remove perk: ${perk.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {catalog.map((entry) => {
              const title = language === "lt" ? entry.titleLt : entry.titleEn;
              const description =
                language === "lt" ? entry.descriptionLt : entry.descriptionEn;
              const active = perks.some((p) => p.id === entry.id && p.enabled);
              return (
                <div
                  key={entry.id}
                  className={cn(
                    "flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors",
                    active ? "border-border bg-background" : "border-dashed border-border bg-muted/30"
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{title}</div>
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground tabular-nums">
                        {entry.amount}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                      {description}
                    </p>
                  </div>
                  <Switch
                    checked={active}
                    disabled={updateProposal.isPending || isReadOnly || !targetProposal}
                    onCheckedChange={(checked) => togglePerk(entry.id, checked)}
                    aria-label={`Enable perk: ${title}`}
                  />
                </div>
              );
            })}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function OpportunityQuoteBuilder({
  opportunityId,
  industryType,
  language,
  proposals,
  proposalsLoading,
  selectedProductCards,
  productsByStripeId,
  productCategorySummary,
  onPersistAdd,
  onPersistRemove,
  persistMutating,
}: {
  opportunityId: string;
  industryType: IndustryType | null;
  language: "lt" | "en";
  proposals: ProposalRowForQuote[];
  proposalsLoading: boolean;
  selectedProductCards: SelectedProductCard[];
  productsByStripeId: Map<string, ProductInfo>;
  productCategorySummary: {
    counts: { seats: number; core: number; hub: number; onboarding: number; other: number };
    errors: string[];
    hasItems: boolean;
  };
  onPersistAdd: (stripeProductId: string, kind: "main" | "extra") => void;
  onPersistRemove: (stripeProductId: string, kind: "main" | "extra") => void;
  persistMutating: boolean;
}) {
  const utils = trpc.useUtils();
  const services = trpc.quoteBuilder.getServices.useQuery();

  const targetProposal = useMemo(() => {
    if (proposals.length === 0) return null;
    const editable = [...proposals].reverse().find((p) => p.status !== "accepted");
    return editable ?? proposals[proposals.length - 1];
  }, [proposals]);

  const isReadOnly = !!targetProposal && targetProposal.status === "accepted";

  const updateProposal = trpc.proposals.update.useMutation({
    onSuccess: () => {
      utils.proposals.list.invalidate({ opportunityId });
    },
    onError: (e) => toast.error(e.message ?? "Failed to save quote"),
  });

  const [state, setState] = useState<QuoteState | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const lastSavedRef = useRef<string>("");
  const seededProposalIdRef = useRef<string | null>(null);

  const [expiresAtInput, setExpiresAtInput] = useState("");
  const seededExpiresProposalIdRef = useRef<string | null>(null);

  // Seeds the date input from the proposal's explicit expiresAt, or from the
  // legacy 30-day-from-creation default when none is set yet — same value
  // the public page and generated PDF would show right now.
  useEffect(() => {
    const key = targetProposal?.id ?? "__none__";
    if (seededExpiresProposalIdRef.current === key) return;
    seededExpiresProposalIdRef.current = key;
    setExpiresAtInput(
      targetProposal?.expiresAt
        ? toUtcDateInputValue(targetProposal.expiresAt)
        : defaultExpiresAtInputValue(targetProposal?.createdAt)
    );
  }, [targetProposal]);

  const handleExpiresAtChange = (value: string) => {
    setExpiresAtInput(value);
    if (!targetProposal || !value) return;
    updateProposal.mutate({
      id: targetProposal.id,
      patch: { expiresAt: new Date(`${value}T00:00:00.000Z`).toISOString() },
    });
  };

  const isQuoteExpired = expiresAtInput !== "" && expiresAtInput < toUtcDateInputValue(new Date());

  const serviceMap = useMemo<Map<string, ServiceItem>>(
    () => new Map((services.data ?? []).map((s) => [s.stripeProductId, s])),
    [services.data]
  );

  const annualSupported = useMemo(
    () => (services.data ?? []).some((s) => hasPeriod(s, "annual", industryType)),
    [services.data, industryType]
  );

  // The quote builder is the single source of truth for the proposal's product
  // list. It seeds once from the proposal's own calculatorState (or, before a
  // proposal exists, from the bootstrap selection) and is then driven entirely
  // by the user's edits — it is intentionally NOT re-seeded when the
  // opportunity's selectedProducts / brief change, so regenerating the brief or
  // landing page can never rebuild, reorder, or drop products here.
  useEffect(() => {
    if (services.isLoading || proposalsLoading) return;
    const proposalKey = targetProposal?.id ?? "__none__";
    const proposalChanged = seededProposalIdRef.current !== proposalKey;
    if (state !== null && !proposalChanged) return;

    const proposalCalc = targetProposal?.calculatorState ?? null;
    if (proposalCalc) {
      const period: BillingPeriod = proposalCalc.billing_period ?? "monthly";
      // Re-resolve unit_amount when a saved line has 0 — old proposals saved
      // before tiered-price support stored 0 for tiered products.
      const rehydrate = (line: ProposalProduct): ProposalProduct => {
        const svc = serviceMap.get(line.stripe_product_id);
        const price = svc ? pickPriceForPeriod(svc, period, industryType) : null;
        const next = { ...line };
        // Snap onto the industry-correct price when the stored line points at a
        // different one — e.g. a proposal saved before industry pricing still
        // holds the accounting seat price for a business client. The admin can
        // only edit unit price / discount / qty, never which price applies, so
        // re-resolving here is safe and idempotent once already correct.
        if (
          price?.stripePriceId &&
          line.stripe_price_id !== price.stripePriceId
        ) {
          next.stripe_price_id = price.stripePriceId;
          next.tiers_mode = price.tiersMode ?? null;
          next.tiers = price.tiers ?? null;
          next.usage_type = price.usageType === "metered" ? "metered" : "licensed";
          // Keep a manual discount; otherwise adopt the new price's unit amount.
          if ((next.discount_cents ?? 0) <= 0) {
            next.unit_amount = effectiveUnitAmount(price) ?? next.unit_amount;
          }
        }
        // Old proposals saved before tiered-price support stored 0 for the
        // unit_amount and no tier data. Re-resolve both when missing.
        if (!next.unit_amount || next.unit_amount <= 0) {
          const resolved = effectiveUnitAmount(price) ?? svc?.defaultPriceCents ?? 0;
          if (resolved > 0) next.unit_amount = resolved;
        }
        if (next.tiers == null && price?.tiers) {
          next.tiers_mode = price.tiersMode ?? null;
          next.tiers = price.tiers;
        }
        if (next.usage_type == null && price) {
          next.usage_type = price.usageType === "metered" ? "metered" : "licensed";
        }
        // Flat-fee plans (e.g. the business seat plan) are billed per account,
        // so the amount is always 1 regardless of seat count.
        if (priceTiersAreFlatFee(next.tiers_mode, next.tiers)) {
          next.qty = 1;
        }
        return next;
      };
      const existingExtras = (proposalCalc.extra ?? []).map((p) => rehydrate({ ...p }));
      const existingExtraIds = new Set(existingExtras.map((p) => p.stripe_product_id));
      const mainIds = new Set(
        (proposalCalc.main ?? []).map((p) => p.stripe_product_id)
      );
      const catalogExtras: ProposalProduct[] = [];
      for (const svc of services.data ?? []) {
        if (svc.subCategory !== "hub" && svc.subCategory !== "onboarding") continue;
        if (existingExtraIds.has(svc.stripeProductId)) continue;
        if (mainIds.has(svc.stripeProductId)) continue;
        const price = pickPriceForPeriod(svc, period, industryType);
        const usageType = price?.usageType === "metered" ? "metered" : "licensed";
        const isFlatFee = priceTiersAreFlatFee(price?.tiersMode, price?.tiers);
        catalogExtras.push({
          stripe_product_id: svc.stripeProductId,
          stripe_price_id: price?.stripePriceId ?? svc.defaultPriceId ?? "",
          qty: isFlatFee ? 1 : svc.defaultQty ?? 1,
          usage: 0,
          unit_amount: effectiveUnitAmount(price) ?? svc.defaultPriceCents ?? 0,
          usage_type: usageType,
          tiers_mode: price?.tiersMode ?? null,
          tiers: price?.tiers ?? null,
          name: svc.name,
          selected: false,
        });
      }
      const next: QuoteState = {
        billing_period: period,
        main: (proposalCalc.main ?? []).map((p) => rehydrate({ ...p })),
        extra: [...existingExtras, ...catalogExtras],
        currency: proposalCalc.currency ?? "EUR",
      };
      setState(next);
      lastSavedRef.current = JSON.stringify(next);
    } else {
      // No proposal yet — bootstrap the draft from the opportunity's current
      // selection. Once the skeleton proposal is created the effect re-seeds
      // from its calculatorState and this branch is never taken again.
      const period = state?.billing_period ?? "monthly";
      const prevExtraSelected = new Map<string, boolean>();
      const prevExtraHidden = new Map<string, boolean>();
      for (const e of state?.extra ?? []) {
        if (e.selected !== undefined) {
          prevExtraSelected.set(e.stripe_product_id, e.selected);
        }
        if (e.hidden !== undefined) {
          prevExtraHidden.set(e.stripe_product_id, e.hidden);
        }
      }
      const seeded = buildSeedState(
        services.data ?? [],
        selectedProductCards,
        period,
        industryType,
        prevExtraSelected,
        prevExtraHidden
      );
      setState(seeded);
      lastSavedRef.current = targetProposal ? "" : JSON.stringify(seeded);
    }
    seededProposalIdRef.current = proposalKey;
  }, [
    services.data,
    services.isLoading,
    proposalsLoading,
    targetProposal,
    selectedProductCards,
    industryType,
    state,
  ]);

  useEffect(() => {
    if (!state || !targetProposal || isReadOnly) return;
    const serialized = JSON.stringify(state);
    if (serialized === lastSavedRef.current) return;
    const handle = window.setTimeout(() => {
      lastSavedRef.current = serialized;
      updateProposal.mutate({
        id: targetProposal.id,
        patch: { calculatorState: state as unknown as Record<string, unknown> },
      });
    }, 600);
    return () => window.clearTimeout(handle);
  }, [state, targetProposal, isReadOnly, updateProposal]);

  const handlePeriodChange = (period: BillingPeriod) => {
    setState((prev) => {
      if (!prev) return prev;
      if (prev.billing_period === period) return prev;
      const remap = (line: ProposalProduct): ProposalProduct => {
        const svc = serviceMap.get(line.stripe_product_id);
        if (!svc) return line;
        const price = pickPriceForPeriod(svc, period, industryType);
        if (!price) return line;
        const usageType = price.usageType === "metered" ? "metered" : "licensed";
        const isFlatFee = priceTiersAreFlatFee(price.tiersMode, price.tiers);
        return {
          ...line,
          stripe_price_id: price.stripePriceId ?? line.stripe_price_id,
          unit_amount: effectiveUnitAmount(price) ?? line.unit_amount,
          usage_type: usageType,
          tiers_mode: price.tiersMode ?? null,
          tiers: price.tiers ?? null,
          qty: isFlatFee ? 1 : line.qty,
        };
      };
      return {
        ...prev,
        billing_period: period,
        main: prev.main.map(remap),
        extra: prev.extra.map(remap),
      };
    });
  };

  const updateLine = (
    kind: "main" | "extra",
    stripeProductId: string,
    patch: Partial<ProposalProduct & { selected?: boolean }>
  ) => {
    setState((prev) => {
      if (!prev) return prev;
      const arr = kind === "main" ? prev.main : prev.extra;
      const next = arr.map((l) =>
        l.stripe_product_id === stripeProductId ? { ...l, ...patch } : l
      );
      return kind === "main" ? { ...prev, main: next } : { ...prev, extra: next };
    });
  };

  const removeLine = (kind: "main" | "extra", stripeProductId: string) => {
    setState((prev) => {
      if (!prev) return prev;
      if (kind === "main") {
        return {
          ...prev,
          main: prev.main.filter((l) => l.stripe_product_id !== stripeProductId),
        };
      }
      return {
        ...prev,
        extra: prev.extra.filter((l) => l.stripe_product_id !== stripeProductId),
      };
    });
    if (!targetProposal) {
      onPersistRemove(stripeProductId, kind);
    }
  };

  // Hub services and onboarding are auto-seeded into every proposal as opt-in
  // extras. Hiding lets the admin tuck the irrelevant ones away per opportunity
  // without deleting them (a delete would just be re-seeded on reload). Hidden
  // lines persist in calculatorState and are restorable from the collapsible
  // "Hidden services" list. Default for every line is visible.
  const canHideExtra = (stripeProductId: string) => {
    const sub = productsByStripeId.get(stripeProductId)?.subCategory;
    return sub === "hub" || sub === "onboarding";
  };

  const hideLine = (stripeProductId: string) =>
    updateLine("extra", stripeProductId, { hidden: true, selected: false });

  const restoreLine = (stripeProductId: string) =>
    updateLine("extra", stripeProductId, { hidden: false });

  const addProduct = (svc: ServiceItem) => {
    setState((prev) => {
      if (!prev) return prev;
      const existsMain = prev.main.some((l) => l.stripe_product_id === svc.stripeProductId);
      const existsExtra = prev.extra.some((l) => l.stripe_product_id === svc.stripeProductId);
      if (existsMain || existsExtra) return prev;
      const price = pickPriceForPeriod(svc, prev.billing_period, industryType);
      const usageType = price?.usageType === "metered" ? "metered" : "licensed";
      const isFlatFee = priceTiersAreFlatFee(price?.tiersMode, price?.tiers);
      const line: ProposalProduct = {
        stripe_product_id: svc.stripeProductId,
        stripe_price_id: price?.stripePriceId ?? svc.defaultPriceId ?? "",
        qty: isFlatFee ? 1 : svc.defaultQty ?? 1,
        usage: 0,
        unit_amount: effectiveUnitAmount(price) ?? svc.defaultPriceCents ?? 0,
        usage_type: usageType,
        tiers_mode: price?.tiersMode ?? null,
        tiers: price?.tiers ?? null,
        name: svc.name,
      };
      if (svc.category === "extra" || svc.category === "accounting_system") {
        return { ...prev, extra: [...prev.extra, { ...line, selected: true }] };
      }
      return { ...prev, main: [...prev.main, line] };
    });
    setAddOpen(false);
    if (!targetProposal) {
      const kind: "main" | "extra" =
        svc.category === "extra" || svc.category === "accounting_system"
          ? "extra"
          : "main";
      onPersistAdd(svc.stripeProductId, kind);
    }
  };

  const allLines: QuoteLine[] = useMemo(() => {
    if (!state) return [];
    return [
      ...state.main.map((l) => ({ ...l, kind: "main" as const })),
      ...state.extra.map((l) => ({ ...l, kind: "extra" as const })),
    ];
  }, [state]);

  // Discounts live on the quote line itself (calculatorState), not on the
  // opportunity. Building the patch here keeps the discounted unit price, the
  // pre-discount original, and the discount marker in sync on a single line.
  const buildDiscountPatch = (
    line: ProposalProduct,
    discountCents: number
  ): Partial<ProposalProduct> => {
    const hadDiscount = (line.discount_cents ?? 0) > 0;
    const original = hadDiscount
      ? line.original_unit_amount ?? line.unit_amount
      : line.unit_amount;
    if (discountCents > 0 && discountCents !== original) {
      return {
        unit_amount: discountCents,
        original_unit_amount: original,
        discount_cents: discountCents,
      };
    }
    return {
      unit_amount: original,
      original_unit_amount: original,
      discount_cents: 0,
    };
  };

  const isAccountingSystem = (stripeProductId: string) =>
    productsByStripeId.get(stripeProductId)?.category === "accounting_system";

  const mainLines = useMemo(
    () =>
      allLines.filter(
        (l) => l.kind === "main" && !isAccountingSystem(l.stripe_product_id)
      ),
    [allLines, productsByStripeId]
  );
  const extraLines = useMemo(
    () =>
      allLines.filter(
        (l) => l.kind === "extra" && !isAccountingSystem(l.stripe_product_id)
      ),
    [allLines, productsByStripeId]
  );
  const accountingSystemLines = useMemo(
    () => allLines.filter((l) => isAccountingSystem(l.stripe_product_id)),
    [allLines, productsByStripeId]
  );
  const groupedMainLines = useMemo(
    () => groupProductsByCategory(mainLines, productsByStripeId),
    [mainLines, productsByStripeId]
  );
  const groupedExtraLines = useMemo(
    () => groupProductsByCategory(extraLines, productsByStripeId),
    [extraLines, productsByStripeId]
  );
  const hasAnyLines =
    mainLines.length > 0 ||
    extraLines.length > 0 ||
    accountingSystemLines.length > 0;

  const availableProducts = useMemo(() => {
    const inState = new Set([
      ...(state?.main ?? []).map((l) => l.stripe_product_id),
      ...(state?.extra ?? []).map((l) => l.stripe_product_id),
    ]);
    return (services.data ?? []).filter((s) => !inState.has(s.stripeProductId));
  }, [services.data, state]);

  const groupedAvailableProducts = useMemo(() => {
    const buckets: Record<ProductGroupKey, ServiceItem[]> = {
      seats: [],
      core: [],
      hub: [],
      onboarding: [],
      accounting_system: [],
      other: [],
    };
    for (const s of availableProducts) {
      if (s.category === "accounting_system") {
        buckets.accounting_system.push(s);
        continue;
      }
      const sub = s.subCategory;
      if (sub === "seats") buckets.seats.push(s);
      else if (sub === "core") buckets.core.push(s);
      else if (sub === "hub") buckets.hub.push(s);
      else if (sub === "onboarding") buckets.onboarding.push(s);
      else buckets.other.push(s);
    }
    return PRODUCT_GROUP_ORDER.filter((g) => buckets[g.key].length > 0).map((g) => ({
      key: g.key,
      label: g.label,
      items: buckets[g.key],
    }));
  }, [availableProducts]);

  const isOnboardingLine = (l: ProposalProduct) =>
    productsByStripeId.get(l.stripe_product_id)?.subCategory === "onboarding";

  const grandTotal = useMemo(() => {
    if (!state) return 0;
    const sumLine = (s: number, l: ProposalProduct) =>
      s + computeLineTotalCents(l, l.qty + (l.usage ?? 0));
    const mainSum = state.main
      .filter((l) => !isOnboardingLine(l))
      .reduce(sumLine, 0);
    const extraSum = state.extra
      .filter((l) => l.selected && !isOnboardingLine(l))
      .reduce(sumLine, 0);
    return mainSum + extraSum;
  }, [state, productsByStripeId]);

  const oneTimeTotal = useMemo(() => {
    if (!state) return 0;
    const sumLine = (s: number, l: ProposalProduct) =>
      s + computeLineTotalCents(l, l.qty + (l.usage ?? 0));
    const mainSum = state.main.filter(isOnboardingLine).reduce(sumLine, 0);
    // Onboarding extras are opt-in just like recurring extras: an unselected
    // add-on must not count toward the one-time total (matches the customer
    // calculator, where the toggle gates one-time extras too).
    const extraSum = state.extra
      .filter((l) => l.selected && isOnboardingLine(l))
      .reduce(sumLine, 0);
    return mainSum + extraSum;
  }, [state, productsByStripeId]);

  const periodSuffix = state?.billing_period === "annual" ? "/yr" : "/mo";
  const totalLabel =
    state?.billing_period === "annual" ? "Total annual fee" : "Total monthly fee";

  const loading = services.isLoading || proposalsLoading || !state;
  const isDirty =
    !!state && !!targetProposal && JSON.stringify(state) !== lastSavedRef.current;
  const saveStatus = !targetProposal
    ? "Draft — create a proposal to persist"
    : isReadOnly
      ? "Read-only (proposal accepted)"
      : updateProposal.isPending
        ? "Saving…"
        : isDirty
          ? "Unsaved changes…"
          : "Saved";

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
          <Package className="h-3.5 w-3.5" />
          Quote builder
          <span className="ml-auto hidden text-[10px] font-medium normal-case tracking-normal text-muted-foreground sm:inline">
            {saveStatus}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {productCategorySummary.hasItems && (
          <div
            className={cn(
              "rounded-md border px-3 py-2 text-xs",
              productCategorySummary.errors.length > 0
                ? "border-rose-300 bg-rose-50 text-rose-800"
                : "border-emerald-300 bg-emerald-50 text-emerald-800"
            )}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
              <span>SEAT PLAN: {productCategorySummary.counts.seats}</span>
              <span>STORAGE PLAN: {productCategorySummary.counts.core}/1</span>
              <span>HUB SERVICES: {productCategorySummary.counts.hub}</span>
              <span>ONBOARDING: {productCategorySummary.counts.onboarding}</span>
            </div>
            {productCategorySummary.errors.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {productCategorySummary.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card p-3">
          <div>
            <div className="text-sm font-medium">Billing period</div>
            <div className="text-[11px] text-muted-foreground">
              Applies to every line in this quote
            </div>
          </div>
          <div className="inline-flex rounded-lg border p-0.5">
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition",
                (state?.billing_period ?? "monthly") === "monthly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => handlePeriodChange("monthly")}
              disabled={isReadOnly}
            >
              Monthly
            </button>
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition",
                state?.billing_period === "annual"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
                (!annualSupported || isReadOnly) && "cursor-not-allowed opacity-50"
              )}
              disabled={!annualSupported || isReadOnly}
              onClick={() => annualSupported && handlePeriodChange("annual")}
              title={annualSupported ? undefined : "No products with an annual plan"}
            >
              Annual
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card p-3">
          <div>
            <div className="text-sm font-medium">Quote expiration</div>
            <div
              className={cn(
                "text-[11px]",
                isQuoteExpired ? "text-rose-600" : "text-muted-foreground"
              )}
            >
              {isQuoteExpired
                ? "Expired — the public link now shows the closed landing page"
                : "The public link shows the live quote until this date"}
            </div>
          </div>
          <Input
            type="date"
            className="w-40"
            value={expiresAtInput}
            onChange={(e) => handleExpiresAtChange(e.target.value)}
            disabled={isReadOnly || !targetProposal}
          />
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : !hasAnyLines ? (
          <div className="rounded-md border border-dashed bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
            No products in the quote yet. Click "Add product" below to build the quote.
          </div>
        ) : groupedMainLines.length === 0 ? null : (
          <div className="space-y-4">
            <div className="hidden items-center gap-3 px-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
              <div className="w-8 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1" aria-hidden />
              <div className="w-24 shrink-0 text-right">Unit price (€)</div>
              <div
                className="w-24 shrink-0 text-right"
                title="New discounted unit price in EUR — not the amount off. Leave blank to keep the original price."
              >
                Discounted price (€)
              </div>
              <div className="w-24 shrink-0 text-right">Amount</div>
              <div className="w-24 shrink-0 text-right">Line total</div>
              <div className="w-7 shrink-0" aria-hidden />
              <div className="w-7 shrink-0" aria-hidden />
            </div>
            {groupedMainLines.map((group) => (
              <div key={group.key} className="space-y-2">
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h4>
                <div className="space-y-2">
                  {group.items.map((line) => {
                    return (
                      <QuoteLineRow
                        key={`${line.kind}-${line.stripe_product_id}`}
                        line={line}
                        language={language}
                        productInfo={productsByStripeId.get(line.stripe_product_id)}
                        periodSuffix={periodSuffix}
                        readOnly={isReadOnly}
                        discountCents={line.discount_cents ?? 0}
                        onChangeQty={(qty) =>
                          updateLine(line.kind, line.stripe_product_id, { qty })
                        }
                        onChangeUnit={(unit_amount) =>
                          updateLine(line.kind, line.stripe_product_id, { unit_amount })
                        }
                        onChangeDiscount={(cents) =>
                          updateLine(
                            line.kind,
                            line.stripe_product_id,
                            buildDiscountPatch(line, cents)
                          )
                        }
                        onToggleSelected={(selected) =>
                          line.kind === "extra"
                            ? updateLine(line.kind, line.stripe_product_id, { selected })
                            : undefined
                        }
                        onDelete={() => removeLine(line.kind, line.stripe_product_id)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={
                isReadOnly ||
                services.isLoading ||
                persistMutating ||
                availableProducts.length === 0
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add product
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search products…" />
              <CommandList>
                <CommandEmpty>No matching products.</CommandEmpty>
                {groupedAvailableProducts.map((group) => (
                  <CommandGroup key={group.key} heading={group.label}>
                    {group.items.map((s) => {
                      const price = pickPriceForPeriod(
                        s,
                        state?.billing_period ?? "monthly",
                        industryType
                      );
                      const amount = effectiveUnitAmount(price) ?? s.defaultPriceCents;
                      const info = productsByStripeId.get(s.stripeProductId);
                      const shown = translateProductName(language, info, s.name);
                      return (
                        <CommandItem
                          key={s.stripeProductId}
                          value={`${shown} ${s.name}`}
                          onSelect={() => addProduct(s)}
                        >
                          <div className="flex w-full items-center justify-between gap-2">
                            <span className="truncate">{shown}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {amount != null ? `${formatEur(amount)}${periodSuffix}` : "—"}
                            </span>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-xs text-muted-foreground sm:hidden">{saveStatus}</div>
          <div className="ml-auto text-right">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {totalLabel}
            </div>
            <div className="text-xl font-bold">
              {formatEur(grandTotal)}
              <span className="text-sm font-normal text-muted-foreground">
                {periodSuffix}
              </span>
            </div>
            {oneTimeTotal > 0 && (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  One-time
                </div>
                <div className="text-sm font-semibold text-muted-foreground">
                  {formatEur(oneTimeTotal)}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

    {!loading && groupedExtraLines.length > 0 && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
            <Plus className="h-3.5 w-3.5" />
            Extra services
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="hidden items-center gap-3 px-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
              <div className="w-8 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1" aria-hidden />
              <div className="w-24 shrink-0 text-right">Unit price (€)</div>
              <div
                className="w-24 shrink-0 text-right"
                title="New discounted unit price in EUR — not the amount off. Leave blank to keep the original price."
              >
                Discounted price (€)
              </div>
              <div className="w-24 shrink-0 text-right">Amount</div>
              <div className="w-24 shrink-0 text-right">Line total</div>
              <div className="w-7 shrink-0" aria-hidden />
              <div className="w-7 shrink-0" aria-hidden />
            </div>
            {groupedExtraLines.map((group) => {
              const visibleItems = group.items.filter((l) => !l.hidden);
              const hiddenItems = group.items.filter((l) => l.hidden);
              return (
                <div key={group.key} className="space-y-2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h4>
                  <div className="space-y-2">
                    {visibleItems.map((line) => {
                      return (
                        <QuoteLineRow
                          key={`${line.kind}-${line.stripe_product_id}`}
                          line={line}
                          language={language}
                          productInfo={productsByStripeId.get(line.stripe_product_id)}
                          periodSuffix={periodSuffix}
                          readOnly={isReadOnly}
                          discountCents={line.discount_cents ?? 0}
                          onChangeQty={(qty) =>
                            updateLine(line.kind, line.stripe_product_id, { qty })
                          }
                          onChangeUnit={(unit_amount) =>
                            updateLine(line.kind, line.stripe_product_id, { unit_amount })
                          }
                          onChangeDiscount={(cents) =>
                            updateLine(
                              line.kind,
                              line.stripe_product_id,
                              buildDiscountPatch(line, cents)
                            )
                          }
                          onToggleSelected={(selected) =>
                            updateLine(line.kind, line.stripe_product_id, { selected })
                          }
                          onHide={
                            canHideExtra(line.stripe_product_id)
                              ? () => hideLine(line.stripe_product_id)
                              : undefined
                          }
                          onDelete={() => removeLine(line.kind, line.stripe_product_id)}
                        />
                      );
                    })}
                  </div>
                  {hiddenItems.length > 0 && (
                    <HiddenExtraServices
                      items={hiddenItems}
                      language={language}
                      productsByStripeId={productsByStripeId}
                      readOnly={isReadOnly}
                      onRestore={(stripeProductId) => restoreLine(stripeProductId)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    )}

    {!loading && accountingSystemLines.length > 0 && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
            <Plus className="h-3.5 w-3.5" />
            Accounting system
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="hidden items-center gap-3 px-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
              <div className="w-8 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1" aria-hidden />
              <div className="w-24 shrink-0 text-right">Unit price (€)</div>
              <div className="w-24 shrink-0 text-right">Amount</div>
              <div className="w-24 shrink-0 text-right">Line total</div>
              <div className="w-7 shrink-0" aria-hidden />
              <div className="w-7 shrink-0" aria-hidden />
            </div>
            {accountingSystemLines.map((line) => {
              return (
                <QuoteLineRow
                  key={`${line.kind}-${line.stripe_product_id}`}
                  line={line}
                  language={language}
                  productInfo={productsByStripeId.get(line.stripe_product_id)}
                  periodSuffix={periodSuffix}
                  readOnly={isReadOnly}
                  discountCents={line.discount_cents ?? 0}
                  onChangeQty={(qty) =>
                    updateLine(line.kind, line.stripe_product_id, { qty })
                  }
                  onChangeUnit={(unit_amount) =>
                    updateLine(line.kind, line.stripe_product_id, { unit_amount })
                  }
                  onChangeDiscount={(cents) =>
                    updateLine(
                      line.kind,
                      line.stripe_product_id,
                      buildDiscountPatch(line, cents)
                    )
                  }
                  onToggleSelected={(selected) =>
                    line.kind === "extra"
                      ? updateLine(line.kind, line.stripe_product_id, { selected })
                      : undefined
                  }
                  onDelete={() => removeLine(line.kind, line.stripe_product_id)}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>
    )}
    </div>
  );
}

function QuoteLineRow({
  line,
  language,
  productInfo,
  periodSuffix,
  readOnly,
  discountCents,
  onChangeQty,
  onChangeUnit,
  onChangeDiscount,
  onToggleSelected,
  onHide,
  onDelete,
}: {
  line: QuoteLine;
  language: "lt" | "en";
  productInfo?: ProductInfo;
  periodSuffix: string;
  readOnly: boolean;
  discountCents: number;
  onChangeQty: (qty: number) => void;
  onChangeUnit: (unitAmountCents: number) => void;
  onChangeDiscount: (discountCents: number) => void;
  onToggleSelected?: (selected: boolean) => void;
  onHide?: () => void;
  onDelete: () => void;
}) {
  const isExtra = line.kind === "extra";
  const isActive = !isExtra || (line.selected ?? false);
  // Flat-fee plans (e.g. the business seat plan) charge a fixed price per
  // account, so the amount is locked to 1 and never multiplied by seats.
  const isFlatFee = lineIsFlatFee(line);
  const translated = translateProductName(language, productInfo, line.name ?? line.stripe_product_id);
  const hasDiscount = discountCents > 0;
  const originalUnitAmount = line.original_unit_amount ?? line.unit_amount;
  const effectiveUnit = hasDiscount ? discountCents : line.unit_amount;
  const effectiveLine = { ...line, unit_amount: effectiveUnit };
  const lineTotal = computeLineTotalCents(effectiveLine, line.qty + (line.usage ?? 0));
  const originalLineTotal = hasDiscount
    ? computeLineTotalCents(
        { ...line, unit_amount: originalUnitAmount },
        line.qty + (line.usage ?? 0)
      )
    : null;
  const unitInputValue = hasDiscount ? originalUnitAmount : line.unit_amount;
  const [unitDraft, setUnitDraft] = useState<string>(() => (unitInputValue / 100).toString());
  const lastUnitAmountRef = useRef<number>(unitInputValue);
  useEffect(() => {
    if (unitInputValue !== lastUnitAmountRef.current) {
      lastUnitAmountRef.current = unitInputValue;
      setUnitDraft((unitInputValue / 100).toString());
    }
  }, [unitInputValue]);
  const commitUnit = (raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setUnitDraft((unitInputValue / 100).toString());
      return;
    }
    const cents = Math.round(parsed * 100);
    if (cents !== line.unit_amount && !hasDiscount) {
      onChangeUnit(cents);
    }
    setUnitDraft((cents / 100).toString());
  };
  const [discountDraft, setDiscountDraft] = useState<string>(() =>
    discountCents > 0 ? (discountCents / 100).toString() : ""
  );
  const lastDiscountRef = useRef<number>(discountCents);
  useEffect(() => {
    if (discountCents !== lastDiscountRef.current) {
      lastDiscountRef.current = discountCents;
      setDiscountDraft(discountCents > 0 ? (discountCents / 100).toString() : "");
    }
  }, [discountCents]);
  const commitDiscount = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      if (discountCents !== 0) onChangeDiscount(0);
      setDiscountDraft("");
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setDiscountDraft(discountCents > 0 ? (discountCents / 100).toString() : "");
      return;
    }
    const cents = Math.round(parsed * 100);
    if (cents !== discountCents) onChangeDiscount(cents);
    setDiscountDraft(cents > 0 ? (cents / 100).toString() : "");
  };
  return (
    <div
      className={cn(
        "group flex flex-wrap items-center gap-3 rounded-md border p-3 sm:flex-nowrap",
        !isActive && "opacity-60"
      )}
    >
      <div className="flex w-8 shrink-0 items-center justify-center">
        {isExtra && onToggleSelected ? (
          <Switch
            checked={line.selected ?? false}
            onCheckedChange={onToggleSelected}
            disabled={readOnly}
            className="scale-90"
          />
        ) : (
          <Package className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{translated}</span>
          <Badge variant="secondary" className="text-[10px] font-normal">
            {line.kind === "main" ? "Main" : "Extra"}
          </Badge>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {isFlatFee ? `per account${periodSuffix}` : `per unit${periodSuffix}`}
        </div>
      </div>
      <div className="flex w-24 shrink-0 flex-col items-end gap-1">
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground sm:sr-only">
          Unit price (€)
        </label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={unitDraft}
          onChange={(e) => setUnitDraft(e.target.value)}
          onBlur={(e) => commitUnit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          disabled={readOnly || !isActive || hasDiscount}
          title={
            hasDiscount
              ? "Clear discount to edit the unit price directly"
              : undefined
          }
          className={cn(
            "h-8 w-full text-right text-sm",
            hasDiscount && "text-rose-600 line-through decoration-rose-600"
          )}
        />
      </div>
      <div className="flex w-24 shrink-0 flex-col items-end gap-1">
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground sm:sr-only">
          Discounted price (€)
        </label>
        <Input
          type="number"
          min={0}
          step="0.01"
          placeholder="0"
          value={discountDraft}
          onChange={(e) => setDiscountDraft(e.target.value)}
          onBlur={(e) => commitDiscount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          disabled={readOnly || !isActive}
          title="New discounted unit price in EUR — not the amount off. Leave blank to keep the original price. A value equal to the original is treated as no discount."
          className="h-8 w-full text-right text-sm"
        />
      </div>
      <div className="flex w-24 shrink-0 flex-col items-end gap-1">
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground sm:sr-only">
          Amount
        </label>
        <Input
          type="number"
          min={0}
          value={isFlatFee ? 1 : line.qty}
          onChange={(e) => onChangeQty(Math.max(0, Number(e.target.value) || 0))}
          disabled={readOnly || !isActive || isFlatFee}
          title={isFlatFee ? "Flat fee billed per account — amount is fixed at 1" : undefined}
          className="h-8 w-full text-right text-sm"
        />
      </div>
      <div className="w-24 shrink-0 text-right">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground sm:sr-only">
          Line total
        </div>
        {hasDiscount && originalLineTotal != null && originalLineTotal !== lineTotal && (
          <div className="text-[11px] font-medium text-rose-600 line-through decoration-rose-600 tabular-nums">
            {formatEur(originalLineTotal)}
            {periodSuffix}
          </div>
        )}
        <div className={cn("text-sm font-semibold tabular-nums", !isActive && "text-muted-foreground")}>
          {isActive ? `${formatEur(lineTotal)}${periodSuffix}` : "—"}
        </div>
      </div>
      {onHide ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"
          onClick={onHide}
          disabled={readOnly}
          aria-label="Hide service"
          title="Hide — move to the Hidden services list for this opportunity"
        >
          <EyeOff className="h-3.5 w-3.5" />
        </Button>
      ) : (
        <div className="hidden h-7 w-7 shrink-0 sm:block" aria-hidden />
      )}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
        onClick={onDelete}
        disabled={readOnly}
        aria-label="Remove product"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function HiddenExtraServices({
  items,
  language,
  productsByStripeId,
  readOnly,
  onRestore,
}: {
  items: QuoteLine[];
  language: "lt" | "en";
  productsByStripeId: Map<string, ProductInfo>;
  readOnly: boolean;
  onRestore: (stripeProductId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-dashed">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        <EyeOff className="h-3.5 w-3.5" />
        Hidden services ({items.length})
      </button>
      {open && (
        <div className="space-y-1.5 border-t px-3 py-2">
          {items.map((line) => {
            const productInfo = productsByStripeId.get(line.stripe_product_id);
            const translated = translateProductName(
              language,
              productInfo,
              line.name ?? line.stripe_product_id
            );
            return (
              <div
                key={`hidden-${line.stripe_product_id}`}
                className="flex items-center gap-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                  {translated}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => onRestore(line.stripe_product_id)}
                  disabled={readOnly}
                  aria-label="Restore service"
                >
                  <Eye className="h-3.5 w-3.5" />
                  Restore
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProposalStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: "bg-zinc-100 text-zinc-700",
    sent: "bg-blue-100 text-blue-700",
    viewed: "bg-amber-100 text-amber-800",
    accepted: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-700",
  };
  const labels: Record<string, string> = {
    draft: "Draft", sent: "Sent", viewed: "Viewed", accepted: "Accepted", rejected: "Rejected",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", colors[status] ?? "bg-zinc-100 text-zinc-700")}>
      {labels[status] ?? status}
    </span>
  );
}

function ClientContacts({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const list = trpc.contacts.listByClient.useQuery({ clientId });
  const [aiOpen, setAiOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formInitial, setFormInitial] = useState<ContactFormInitial | undefined>(
    undefined
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const rows = list.data ?? [];

  const assignToClient = trpc.contacts.assignToClient.useMutation({
    onSuccess: () => {
      toast.success("Contact assigned");
      utils.contacts.list.invalidate();
      utils.contacts.listByClient.invalidate({ clientId });
    },
    onError: (err) => toast.error(err.message),
  });

  const openCreate = () => {
    setFormMode("create");
    setFormInitial(undefined);
    setFormOpen(true);
  };

  const openEdit = (c: (typeof rows)[number]) => {
    setFormMode("edit");
    setFormInitial({
      id: c.id,
      firstName: c.firstName ?? "",
      lastName: c.lastName ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      mobilePhone: c.mobilePhone ?? "",
      jobTitle: c.jobTitle ?? "",
      linkedinUrl: c.linkedinUrl ?? "",
      notes: c.notes ?? "",
    });
    setFormOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3 space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Contacts ({rows.length})
        </CardTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCreate}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add contact
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            title="Assign an existing contact to this client"
          >
            <Users className="mr-1.5 h-3.5 w-3.5" />
            Assign existing
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAiOpen(true)}
            title="Paste an email signature and let AI extract a contact"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Add with AI
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <AddContactWithAIDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          clientId={clientId}
        />
        <ContactFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          mode={formMode}
          clientId={clientId}
          initial={formInitial}
        />
        <SelectContactDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          clientId={clientId}
          onSelect={(c) => {
            setPickerOpen(false);
            if (c.clientId === clientId) {
              toast.info("Contact already belongs to this client.");
              return;
            }
            assignToClient.mutate({ id: c.id, clientId });
          }}
        />
        {list.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
            No contacts yet for this client. Use “Add contact”, “Assign
            existing”, or “Add with AI”.
          </p>
        ) : (
          rows.map((c) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "—";
            return (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background/60 px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium">{name}</div>
                  {c.jobTitle && (
                    <div className="text-xs text-muted-foreground">{c.jobTitle}</div>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {c.email && (
                    <a className="hover:underline" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                  )}
                  {(c.phone || c.mobilePhone) && (
                    <a className="hover:underline" href={`tel:${c.phone ?? c.mobilePhone ?? ""}`}>
                      {c.phone ?? c.mobilePhone}
                    </a>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openEdit(c)}
                    title="Edit contact"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

// Treats blank values and literal "null"/"undefined" strings (which sneak in
// via CSV/CRM imports) as absent, so they never render in a greeting.
function cleanNameValue(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  if (!t || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") return null;
  return t;
}

function renderTemplateText(
  template: string,
  vars: Record<string, string | null | undefined>
): string {
  return template.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (_, key: string) => {
    const v = vars[key];
    return v == null ? "" : v;
  });
}

// Drops template lines that contain only a single placeholder whose value
// is empty (e.g. `{{user_phone}}` when the sender hasn't set a phone), so
// the signature doesn't render with an awkward blank line. Plain blank
// lines and lines that mix text with placeholders are preserved.
function stripEmptySignatureLines(
  body: string,
  vars: Record<string, string | null | undefined>
): string {
  const placeholderOnly = /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}$/;
  return body
    .split("\n")
    .filter((line) => {
      const m = line.trim().match(placeholderOnly);
      if (!m) return true;
      const v = vars[m[1]];
      return v != null && v !== "";
    })
    .join("\n");
}

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Convert an AI-generated plain-text email body into the simple HTML the rich
 * text editor expects: blank-line-separated blocks become <p>, single newlines
 * become <br>. Text is HTML-escaped first so stray angle brackets stay literal.
 */
function plainTextToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter((para) => para.length > 0)
    .map((para) => `<p>${escape(para).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

function MessageClientDialog({
  open,
  onOpenChange,
  replyContext,
  opportunityId,
  opportunityName,
  clientId,
  defaultEmail,
  contactName,
  contactFirstName,
  contactLastName,
  contactReferenceName,
  clientCompany,
  landingUrl,
  language,
  onSent,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /**
   * When set, the dialog opens as a reply to a client's inbound email: the
   * recipient and subject are pre-filled and the topic-template auto-fill is
   * suppressed so the user writes the reply from a blank body.
   */
  replyContext?: { toEmail: string; subject: string | null } | null;
  opportunityId: string;
  opportunityName: string;
  clientId: string | null;
  defaultEmail: string | null;
  contactName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactReferenceName: string | null;
  clientCompany: string | null;
  landingUrl: string | null;
  language: "lt" | "en";
  onSent: () => void;
}) {
  const templates = trpc.emailTemplates.list.useQuery({ language }, { enabled: open });
  const utils = trpc.useUtils();
  const me = trpc.auth.me.useQuery(undefined, { enabled: open });
  const contactsQuery = trpc.contacts.listByClient.useQuery(
    { clientId: clientId ?? "" },
    { enabled: open && !!clientId }
  );
  const [topic, setTopic] = useState<"proposal" | "follow_up" | "contract" | "random">("proposal");
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [attachments, setAttachments] = useState<
    { name: string; content: string; size: number }[]
  >([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  // When on, the prior conversation (sent emails + client replies) is appended
  // as a quoted history block so the client receives the whole thread.
  const [includeHistory, setIncludeHistory] = useState(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Documents already stored on the opportunity. Only rows with stored binary
  // content (fileSize set) can be attached — external-URL-only docs can't.
  const documentsQuery = trpc.documents.list.useQuery(
    { opportunityId },
    { enabled: open }
  );
  const attachableDocs = useMemo(
    () =>
      (documentsQuery.data ?? []).filter(
        (d) => d.fileName && d.fileSize != null && d.fileSize > 0
      ),
    [documentsQuery.data]
  );
  const selectedDocs = useMemo(
    () => attachableDocs.filter((d) => selectedDocIds.includes(d.id)),
    [attachableDocs, selectedDocIds]
  );

  // Keep the combined attachment payload under ~10MB per email.
  const MAX_ATTACHMENTS_BYTES = 10 * 1024 * 1024;

  const contactByEmail = useMemo(() => {
    const map = new Map<
      string,
      { firstName: string | null; lastName: string | null; email: string }
    >();
    for (const c of contactsQuery.data ?? []) {
      if (c.email) {
        map.set(c.email.toLowerCase(), {
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email,
        });
      }
    }
    return map;
  }, [contactsQuery.data]);

  const primaryContactName = useMemo(() => {
    if (toEmails.length === 1) {
      const c = contactByEmail.get(toEmails[0]!.toLowerCase());
      const fromContact = cleanNameValue(c?.firstName) ?? cleanNameValue(c?.lastName);
      if (c && fromContact) return fromContact;
    }
    const first = cleanNameValue(contactFirstName);
    if (first) return first;
    return cleanNameValue(contactName)?.split(/\s+/)[0] ?? null;
  }, [toEmails, contactByEmail, contactFirstName, contactName]);

  const trimmedReferenceName = cleanNameValue(contactReferenceName) ?? "";
  const greetingName = trimmedReferenceName
    ? trimmedReferenceName
    : language === "lt"
      ? toLithuanianVocative(primaryContactName)
      : primaryContactName ?? "";

  const userFirstName = (me.data?.name ?? "").trim().split(/\s+/)[0] ?? "";

  const vars = useMemo(
    () => ({
      company: clientCompany ?? "",
      contact_name: greetingName,
      contact_first_name: cleanNameValue(contactFirstName) ?? "",
      contact_last_name: cleanNameValue(contactLastName) ?? "",
      contact_reference_name: greetingName,
      opportunity_name: opportunityName ?? "",
      landing_url: landingUrl ?? "",
      user_name: userFirstName,
      user_phone: me.data?.phone ?? "",
      user_email: me.data?.email ?? "",
    }),
    [
      clientCompany,
      greetingName,
      contactFirstName,
      contactLastName,
      trimmedReferenceName,
      opportunityName,
      landingUrl,
      userFirstName,
      me.data,
    ]
  );

  useEffect(() => {
    if (!open) {
      setAttachments([]);
      setAttachmentError(null);
      setSelectedDocIds([]);
    }
  }, [open]);

  // Reply mode: pre-fill recipient + "Re: <subject>" and leave the body blank
  // for the user to write. Runs once per open and short-circuits the
  // contact/template auto-fill effects below.
  useEffect(() => {
    if (!open || !replyContext) return;
    setTopic("random");
    setToEmails([replyContext.toEmail]);
    const base = (replyContext.subject ?? "").replace(/^(re:\s*)+/i, "").trim();
    setSubject(base ? `Re: ${base}` : "Re:");
    setBody("");
  }, [open, replyContext]);

  useEffect(() => {
    if (!open || replyContext) return;
    const contactEmails = (contactsQuery.data ?? [])
      .map((c) => c.email)
      .filter((e): e is string => !!e);
    if (contactEmails.length > 0) {
      setToEmails(contactEmails);
    } else if (defaultEmail) {
      setToEmails([defaultEmail]);
    } else {
      setToEmails([]);
    }
  }, [open, replyContext, contactsQuery.data, defaultEmail]);

  useEffect(() => {
    if (!open || replyContext) return;
    if (me.isLoading) return;
    const tpls = templates.data;
    if (!tpls) return;
    const tpl = tpls.find((t) => t.topic === topic);
    if (!tpl) return;
    setSubject(renderTemplateText(tpl.subject, vars));
    // Plain-text bodies drop empty signature lines; HTML bodies are rendered
    // as-is (line-based stripping would corrupt the markup).
    setBody(
      tpl.format === "html"
        ? renderTemplateText(tpl.body, vars)
        : renderTemplateText(stripEmptySignatureLines(tpl.body, vars), vars)
    );
  }, [open, replyContext, topic, templates.data, vars, me.isLoading]);

  const send = trpc.opportunities.sendMessage.useMutation({
    onSuccess: () => {
      toast.success("Message sent");
      onSent();
    },
    onError: (e) => toast.error(e.message ?? "Failed to send"),
  });

  const draft = trpc.opportunities.draftMessageFromNotes.useMutation({
    onError: (e) => toast.error(e.message ?? "Failed to draft email"),
  });

  const handleDraftWithAi = () => {
    draft.mutate(
      {
        opportunityId,
        topic,
        language,
        contactName: greetingName || undefined,
        landingUrl: landingUrl ?? undefined,
        sender: {
          name: userFirstName || me.data?.name || undefined,
          phone: me.data?.phone || undefined,
          email: me.data?.email || undefined,
        },
      },
      {
        onSuccess: (result) => {
          if (result.subject) setSubject(result.subject);
          // The AI returns a plain-text body. When the active template is HTML
          // the editor expects markup, so convert paragraphs/line breaks; the
          // text editor takes the plain text as-is.
          setBody(format === "html" ? plainTextToHtml(result.body) : result.body);
          toast.success("Draft ready — review and edit before sending.");
        },
      }
    );
  };

  const resetTemplate = trpc.emailTemplates.reset.useMutation({
    onSuccess: () => {
      toast.success("Template reset to default");
      utils.emailTemplates.list.invalidate();
    },
    onError: (e) => toast.error(e.message ?? "Failed to reset template"),
  });

  const currentTpl = templates.data?.find((t) => t.topic === topic);
  const format: "text" | "html" = currentTpl?.format ?? "text";
  const bodyEmpty = format === "html" ? isRichTextEmpty(body) : !body.trim();
  const canReset = currentTpl && !currentTpl.isDefault && me.data?.role === "admin";

  const removeEmail = (email: string) => {
    setToEmails((xs) => xs.filter((e) => e !== email));
  };

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    if (toEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase())) return;
    setToEmails((xs) => [...xs, trimmed]);
    setEmailInput("");
  };

  const availableContacts = (contactsQuery.data ?? []).filter(
    (c) =>
      c.email &&
      !toEmails.some((e) => e.toLowerCase() === c.email!.toLowerCase())
  );

  const readFileAsBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the "data:<mime>;base64," prefix — the server wants raw base64.
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttachmentError(null);
    try {
      const incoming = await Promise.all(
        Array.from(files).map(async (file) => ({
          name: file.name,
          content: await readFileAsBase64(file),
          size: file.size,
        }))
      );
      const docsBytes = selectedDocs.reduce((sum, d) => sum + (d.fileSize ?? 0), 0);
      setAttachments((prev) => {
        const existing = new Set(prev.map((a) => a.name));
        const merged = [...prev];
        for (const a of incoming) {
          if (!existing.has(a.name)) merged.push(a);
        }
        const total = merged.reduce((sum, a) => sum + a.size, 0) + docsBytes;
        if (total > MAX_ATTACHMENTS_BYTES) {
          setAttachmentError("Attachments exceed the 10MB total limit.");
          return prev;
        }
        if (merged.length + selectedDocs.length > 10) {
          setAttachmentError("A message can include at most 10 attachments.");
          return prev;
        }
        return merged;
      });
    } catch {
      setAttachmentError("Failed to read file.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (name: string) => {
    setAttachments((prev) => prev.filter((a) => a.name !== name));
    setAttachmentError(null);
  };

  const toggleDoc = (docId: string) => {
    setAttachmentError(null);
    setSelectedDocIds((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      }
      const doc = attachableDocs.find((d) => d.id === docId);
      const uploadedBytes = attachments.reduce((sum, a) => sum + a.size, 0);
      const docsBytes = selectedDocs.reduce((sum, d) => sum + (d.fileSize ?? 0), 0);
      const next = uploadedBytes + docsBytes + (doc?.fileSize ?? 0);
      if (next > MAX_ATTACHMENTS_BYTES) {
        setAttachmentError("Attachments exceed the 10MB total limit.");
        return prev;
      }
      if (attachments.length + prev.length + 1 > 10) {
        setAttachmentError("A message can include at most 10 attachments.");
        return prev;
      }
      return [...prev, docId];
    });
  };

  const handleSubmit = () => {
    if (toEmails.length === 0 || !subject.trim() || bodyEmpty) return;
    send.mutate({
      opportunityId,
      topic,
      to: toEmails,
      subject: subject.trim(),
      body,
      format,
      includeHistory,
      attachments:
        attachments.length > 0
          ? attachments.map((a) => ({ name: a.name, content: a.content }))
          : undefined,
      documentIds: selectedDocIds.length > 0 ? selectedDocIds : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {replyContext ? "Reply to client" : "Message to client"}
          </DialogTitle>
          <DialogDescription>
            {replyContext
              ? "Replying to the client's email. Write your message, then send it from sales@noium.com via Gmail."
              : "Pick a topic, edit the text if needed, then send it from sales@noium.com via Gmail."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <Label className="block text-xs uppercase tracking-wide text-muted-foreground">
                What are you writing to customer?
              </Label>
              {canReset && (
                <button
                  type="button"
                  onClick={() => resetTemplate.mutate({ topic, language })}
                  disabled={resetTemplate.isPending}
                  className="text-xs text-muted-foreground underline hover:text-foreground disabled:opacity-50"
                >
                  {resetTemplate.isPending ? "Resetting..." : "Reset to default"}
                </button>
              )}
            </div>
            <Select
              value={topic}
              onValueChange={(v) => setTopic(v as typeof topic)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="proposal">Sending proposal</SelectItem>
                <SelectItem value="follow_up">Following up</SelectItem>
                <SelectItem value="contract">Sending contract</SelectItem>
                <SelectItem value="random">Random email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              To
            </Label>
            <div className="flex min-h-[88px] flex-wrap content-start items-start gap-1.5 rounded-md border border-input bg-background px-2 py-1.5">
              {toEmails.map((email) => (
                <Badge
                  key={email}
                  variant="secondary"
                  className="gap-1 pr-1 font-normal"
                >
                  <span className="text-xs">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={`Remove ${email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addEmail(emailInput);
                  } else if (
                    e.key === "Backspace" &&
                    !emailInput &&
                    toEmails.length > 0
                  ) {
                    removeEmail(toEmails[toEmails.length - 1]!);
                  }
                }}
                onBlur={() => {
                  if (emailInput.trim()) addEmail(emailInput);
                }}
                placeholder={
                  toEmails.length === 0 ? "client@example.com" : "Add email..."
                }
                className="flex-1 min-w-[160px] border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            {availableContacts.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                <span className="text-xs text-muted-foreground">
                  Add contact:
                </span>
                {availableContacts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => addEmail(c.email!)}
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    {joinContactName(c.firstName, c.lastName) || c.email}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              Subject
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <Label className="block text-xs uppercase tracking-wide text-muted-foreground">
                Message
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDraftWithAi}
                disabled={draft.isPending}
                className="h-7 gap-1.5 text-xs"
                title="Draft this email from the opportunity's notes, brief and client profile"
              >
                {draft.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Wand2 className="h-3 w-3" />
                )}
                {draft.isPending ? "Drafting…" : "Draft with AI from notes"}
              </Button>
            </div>
            {format === "html" ? (
              <RichTextEditor
                value={body}
                onChange={setBody}
                allowImages
                className="max-h-[360px] overflow-auto"
              />
            ) : (
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="font-mono text-sm"
              />
            )}
          </div>
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              Attachments
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="gap-1.5"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Add files
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={attachableDocs.length === 0}
                    className="gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Select from opportunity
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  <DropdownMenuLabel>Opportunity files</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {attachableDocs.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No files on this opportunity.
                    </div>
                  ) : (
                    attachableDocs.map((d) => (
                      <DropdownMenuCheckboxItem
                        key={d.id}
                        checked={selectedDocIds.includes(d.id)}
                        onCheckedChange={() => toggleDoc(d.id)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        <span className="truncate">{d.fileName ?? d.name}</span>
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {(attachments.length > 0 || selectedDocs.length > 0) && (
              <ul className="mt-2 space-y-1">
                {attachments.map((a) => (
                  <li
                    key={`file-${a.name}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-input bg-muted/40 px-2 py-1.5 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{a.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        ({formatAttachmentSize(a.size)})
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.name)}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={`Remove ${a.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {selectedDocs.map((d) => (
                  <li
                    key={`doc-${d.id}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-input bg-muted/40 px-2 py-1.5 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{d.fileName ?? d.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        ({formatAttachmentSize(d.fileSize ?? 0)} · from opportunity)
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleDoc(d.id)}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={`Remove ${d.fileName ?? d.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {attachmentError && (
              <p className="mt-1 text-xs text-destructive">{attachmentError}</p>
            )}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeHistory}
              onChange={(e) => setIncludeHistory(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span>Pridėti ankstesnį susirašinėjimą</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              send.isPending ||
              toEmails.length === 0 ||
              !subject.trim() ||
              bodyEmpty
            }
          >
            {send.isPending ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
